import React, { useState, useCallback, useRef, useEffect, useMemo } from "react"
import { Head } from "@inertiajs/react"
// import { useRouter } from "next/navigation"
import ReactFlow, {
  MiniMap,
  Background,
  Panel,
  useNodesState,
  useEdgesState,
  ConnectionLineType,
  applyNodeChanges,
  applyEdgeChanges,
} from "reactflow"
import "reactflow/dist/style.css"
import "@styles/workflow-builder.css"
import { Map as MapIcon, ArrowLeft, Layout, GitFork, Edit2, Check, X, Power, PowerOff, Play } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { useWorkflow } from "@/lib/contexts/workflow-context"
import { DEFAULT_DYNAMIC_DATE_FORMAT } from "@/lib/dateUtils"
import { saveTriggerPositionForNode } from "@/lib/workflowGraphPersistence"
import {
  getDescendantNodeIds,
  measureNodeBottom,
  shiftDescendantPositions,
} from "@/lib/workflowChainLayout"
import { router } from "@inertiajs/react"
import { useToast } from "@/hooks/use-toast"
import { useTheme } from "next-themes"

// Custom Node Components
import TriggerNode from "@/components/workflow-builder/nodes/TriggerNode"
import ActionNode from "@/components/workflow-builder/nodes/ActionNode"
import DataNode from "@/components/workflow-builder/nodes/DataNode"
import AINode from "@/components/workflow-builder/nodes/AINode"
import ResponseNode from "@/components/workflow-builder/nodes/ResponseNode"
import PlaceholderNode from "@/components/workflow-builder/nodes/PlaceholderNode"
import WorkflowNode from "@/components/workflow-builder/nodes/WorkflowNode"
import BranchNode from "@/components/workflow-builder/nodes/BranchNode"
import IfElseNode from "@/components/workflow-builder/nodes/IfElseNode"
import TaskNode from "@/components/workflow-builder/nodes/TaskNode"
import RequestNode from "@/components/workflow-builder/nodes/RequestNode"
import FormNode from "@/components/workflow-builder/nodes/FormNode"
import WaitNode from "@/components/workflow-builder/nodes/WaitNode"
import UpdateNoteNode from "@/components/workflow-builder/nodes/UpdateNoteNode"
import UpdateJobNode from "@/components/workflow-builder/nodes/UpdateJobNode"
import CreateNoteNode from "@/components/workflow-builder/nodes/CreateNoteNode"
import TextNode from "@/components/workflow-builder/nodes/TextNode"
import EmailNode from "@/components/workflow-builder/nodes/EmailNode"

// Node Palette
import NodePalette from "@/components/workflow-builder/NodePalette"
import Sidebar from "@/components/workflow-builder/Sidebar"
import TriggersPanel from "@/components/workflow-builder/TriggersPanel"
import WorkflowHistoryPanel from "@/components/workflow-builder/WorkflowHistoryPanel"
import ConnectionBlocksMenu from "@/components/workflow-builder/ConnectionBlocksMenu"
import ZoomControls from "@/components/workflow-builder/ZoomControls"
import CustomEdge from "@/components/workflow-builder/CustomEdge"
import MockExecutionModal from "@/components/workflow-builder/MockExecutionModal"

// Add this component at the top of the file after the imports
const CustomConnectionLine = ({ fromX, fromY, toX, toY, connectionLineStyle }) => {
  return (
    <g>
      <path
        fill="none"
        stroke={connectionLineStyle.stroke}
        strokeWidth={connectionLineStyle.strokeWidth}
        strokeDasharray={connectionLineStyle.strokeDasharray}
        d={`M${fromX},${fromY} L${toX},${toY}`}
      />
      <rect
        x={toX - 50}
        y={toY - 25}
        width={100}
        height={50}
        rx={4}
        fill="#f0f4f8"
        stroke="#b1b1b7"
        strokeDasharray="5,5"
        strokeWidth={2}
        opacity={0.8}
      />
    </g>
  )
}

const CONNECTION_LINE_STYLE = {
  strokeWidth: 2,
  stroke: "#b1b1b7",
  strokeDasharray: "5,5",
}

const PRO_OPTIONS = {
  hideAttribution: true,
  account: "paid-pro",
  elementsDraggable: true,
}

const DELETE_KEY_CODE = ["Backspace", "Delete"]
const MULTI_SELECTION_KEY_CODE = ["Control", "Meta"]
const SNAP_GRID = [15, 15]
// Cap how far fitView can zoom in on load / auto-layout (1 = 100%)
const FIT_VIEW_MAX_ZOOM = 1
const EXPAND_COLLAPSE_LAYOUT_DELAY_MS = 300

function App({ workflowId, workflow, users, categories, departments, integrations, csrf_token, permissions, twilio, textTemplates, emailTemplates, emailAccounts, envEmailDefault, canSendTexts, canSendEmails }) {
  // const router = useRouter()
  const { 
    currentWorkflow, 
    updateWorkflow, 
    workflows, 
    setCurrentWorkflow, 
    deployWorkflow, 
    deactivateWorkflow,
    createWorkflow,
    addNode,
    addEdge,
    updateNode,
    updateEdge,
    deleteEdge,
    setWorkflows,
    refreshWorkflowData,
    copyNode,
    pasteNode,
    hasCopiedNode,
    getCopiedNodeInfo
  } = useWorkflow()
  const { toast } = useToast()
  const { theme } = useTheme()
  
  // Get background color based on theme
  const getBackgroundColor = () => {
    // Check if we're in dark mode (either explicitly dark or system dark)
    const isDarkMode = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
    const bgColor = isDarkMode ? '#1f2937' : '#f5f5f7'

    return bgColor
  }
  
  // Use the workflow data passed from the controller if available
  const [workflowName, setWorkflowName] = useState(workflow?.name || currentWorkflow?.name || "New Workflow")
  const [isEditingName, setIsEditingName] = useState(false)
  const [tempWorkflowName, setTempWorkflowName] = useState("")
  const titleInputRef = useRef(null)

  const [workflowDescription, setWorkflowDescription] = useState(workflow?.description || currentWorkflow?.description || "")
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const [tempWorkflowDescription, setTempWorkflowDescription] = useState("")
  const descriptionInputRef = useRef(null)

  // Keep track of the current workflow ID to detect actual workflow changes
  const [currentWorkflowId, setCurrentWorkflowId] = useState(null)
  
  // Track hovered node id for keyboard shortcuts without triggering rerenders.
  const hoveredNodeIdRef = useRef(null)
  const nodeStepSaveQueueRef = useRef(new Map())
  const nodeStepSaveGenerationRef = useRef(new Map())
  const expandLayoutTimeoutRef = useRef(null)
  // Set current workflow when component loads or when workflow prop changes
  useEffect(() => {
    let targetWorkflowId = null
    
    if (workflow) {
      targetWorkflowId = workflow.id.toString()
      // If we have workflow data from the controller, use it
      if (targetWorkflowId !== currentWorkflowId) {
        const workflowFromContext = workflows.find((w) => w.id === targetWorkflowId)
        setCurrentWorkflow({
          ...(workflowFromContext || {}),
          id: targetWorkflowId,
          name: workflow.name,
          description: workflow.description || '',
          status: workflow.status || workflowFromContext?.status || 'draft',
          nodes: workflowFromContext?.nodes || [],
          edges: workflowFromContext?.edges || [],
          viewMode: workflowFromContext?.viewMode || 'graph',
        })
        setWorkflowName(workflow.name);
        setWorkflowDescription(workflow.description || "");
        setCurrentWorkflowId(targetWorkflowId);
        setNodesInitialized(false);
      }
    } else if (workflowId && workflowId !== "new") {
      targetWorkflowId = workflowId
      // Fallback to finding workflow in context
      const workflowFromContext = workflows.find(w => w.id === workflowId);
      if (workflowFromContext && targetWorkflowId !== currentWorkflowId) {
        setCurrentWorkflow(workflowFromContext);
        setWorkflowDescription(workflowFromContext.description || "");
        setCurrentWorkflowId(targetWorkflowId);
        setNodesInitialized(false);
      } else if (!workflowFromContext) {
        // Create new workflow if it doesn't exist
        createWorkflow("New Workflow", csrf_token);
      }
    } else if (workflowId === "new" && !currentWorkflowId) {
      // Create new workflow for "new" route, only if we don't have a current workflow
      createWorkflow("New Workflow", csrf_token);
    }
  }, [workflow, workflowId, workflows]) // Remove currentWorkflowId from dependencies

  // Update background color when theme changes
  useEffect(() => {
    // Force a re-render of the Background component when theme changes
    // The getBackgroundColor function will be called on each render
  }, [theme])

  // Listen for system theme changes when theme is set to 'system'
  useEffect(() => {
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const handleChange = () => {
        // Force re-render when system theme changes
        console.log('System theme changed, current theme:', theme)
      }
      
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
  }, [theme])


  // Expose workflow context globally for edge components
  useEffect(() => {
    window.workflowContext = {
      deleteEdge,
      currentWorkflow,
      workflowId: currentWorkflow?.id || workflowId,
      setWorkflows,
      refreshWorkflowData,
      updateWorkflowData: (workflowId, updates) => {
        // Update the workflow context with fresh data from database
        if (currentWorkflow?.id === workflowId) {
          setCurrentWorkflow(prev => 
            prev ? { ...prev, ...updates } : null
          )
        }
        
        // Also update the workflows array
        setWorkflows(prev => 
          prev.map(w => 
            w.id === workflowId 
              ? { ...w, ...updates }
              : w
          )
        )
      }
    }
    
    return () => {
      delete window.workflowContext
    }
  }, [deleteEdge, currentWorkflow, workflowId, setWorkflows, refreshWorkflowData])

  // Initialize nodes and edges from current workflow or controller data
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])
  const edgesRef = useRef(edges)
  const reactFlowInstanceRef = useRef(null)

  useEffect(() => {
    edgesRef.current = edges
  }, [edges])

  const debouncedPersistNodeGraph = useMemo(() => {
    const debounce = (func, wait) => {
      let timeout
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout)
          func(...args)
        }
        clearTimeout(timeout)
        timeout = setTimeout(later, wait)
      }
    }

    return debounce(async (workflowId, nodesToSave, edgesToSave) => {
      try {
        await fetch(`/workflows/${workflowId}/nodes-and-edges`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrf_token,
            Accept: 'application/json',
          },
          body: JSON.stringify({ nodes: nodesToSave, edges: edgesToSave }),
        })
      } catch (error) {
        console.error('Failed to persist node graph state:', error)
      }
    }, 500)
  }, [csrf_token])

  const handleNodeExpandedChange = useCallback((nodeId, expanded) => {
    const oldBottom = measureNodeBottom(nodeId, reactFlowInstanceRef.current, nodes)

    if (expandLayoutTimeoutRef.current) {
      clearTimeout(expandLayoutTimeoutRef.current)
    }

    setNodes((prev) => {
      const updated = prev.map((n) =>
        n.id === nodeId ? { ...n, data: { ...n.data, expanded } } : n
      )
      const workflowId = currentWorkflow?.id || workflow?.id
      if (workflowId) {
        debouncedPersistNodeGraph(workflowId, updated, edgesRef.current)
        const previousNode = prev.find((n) => n.id === nodeId)
        updateNode(workflowId, nodeId, {
          data: { ...(previousNode?.data ?? {}), expanded },
        })
      }
      return updated
    })

    expandLayoutTimeoutRef.current = setTimeout(() => {
      setNodes((prev) => {
        const newBottom = measureNodeBottom(nodeId, reactFlowInstanceRef.current, prev)
        const deltaY = newBottom - oldBottom
        const shifted = shiftDescendantPositions(prev, nodeId, deltaY, edgesRef.current)

        if (shifted === prev || Math.abs(deltaY) < 1) {
          return prev
        }

        const workflowId = currentWorkflow?.id || workflow?.id
        if (workflowId) {
          debouncedPersistNodeGraph(workflowId, shifted, edgesRef.current)
          const descendantIds = getDescendantNodeIds(nodeId, edgesRef.current)
          descendantIds.forEach((descendantId) => {
            const movedNode = shifted.find((n) => n.id === descendantId)
            if (movedNode) {
              updateNode(workflowId, descendantId, { position: movedNode.position })
            }
          })
        }

        return shifted
      })
    }, EXPAND_COLLAPSE_LAYOUT_DELAY_MS)
  }, [
    currentWorkflow,
    workflow,
    debouncedPersistNodeGraph,
    updateNode,
    setNodes,
    nodes,
  ])

  useEffect(() => {
    return () => {
      if (expandLayoutTimeoutRef.current) {
        clearTimeout(expandLayoutTimeoutRef.current)
      }
    }
  }, [])

  const persistNodeGraph = useCallback((nodesToSave) => {
    const workflowId = currentWorkflow?.id || workflow?.id
    if (workflowId) {
      debouncedPersistNodeGraph(workflowId, nodesToSave, edgesRef.current)
    }
  }, [currentWorkflow, workflow, debouncedPersistNodeGraph])

  const resolveTargetWorkflow = useCallback(() => {
    if (currentWorkflow?.id) {
      return currentWorkflow
    }

    if (workflow?.id) {
      return {
        id: workflow.id.toString(),
        name: workflow.name,
        description: workflow.description || '',
        status: workflow.status || 'draft',
        nodes: [],
        edges: [],
      }
    }

    return null
  }, [currentWorkflow, workflow])

  const screenToFlowPosition = useCallback((screenPosition, fallback = { x: 300, y: 100 }) => {
    const instance = reactFlowInstanceRef.current
    if (instance?.screenToFlowPosition) {
      return instance.screenToFlowPosition(screenPosition)
    }
    return fallback
  }, [])

  // Persist edge removals when users delete edges via keyboard/ReactFlow controls.
  const handleEdgesChange = useCallback((changes) => {
    const removedEdgeIds = changes
      .filter((change) => change.type === "remove" && typeof change.id === "string")
      .map((change) => change.id)
      .filter((edgeId) => edgeId.startsWith("edge-"))

    if (removedEdgeIds.length > 0) {
      const resolvedWorkflowId = currentWorkflow?.id || workflow?.id || workflowId
      if (resolvedWorkflowId && resolvedWorkflowId !== "new") {
        removedEdgeIds.forEach((edgeId) => {
          const edgeDatabaseId = edgeId.replace('edge-', '')
          fetch(`/workflows/${resolvedWorkflowId}/edges/${edgeDatabaseId}`, {
            method: 'DELETE',
            headers: {
              'X-CSRF-TOKEN': csrf_token,
              'Accept': 'application/json',
            },
          }).then((response) => {
            if (!response.ok) {
              throw new Error(`Failed to delete edge ${edgeId}: ${response.status}`)
            }
          }).catch((error) => {
            console.error("Failed to persist edge deletion:", error)
            toast({
              title: "Failed to delete connection",
              description: "The connection was removed visually, but could not be deleted from the server.",
              variant: "destructive",
            })
          })
        })
      }
    }

    onEdgesChange(changes)
  }, [onEdgesChange, currentWorkflow?.id, workflow?.id, workflowId, csrf_token, toast, edges])

  // Sync selected edges state when edges change
  useEffect(() => {
    const triggerEdges = edges.filter(edge => {
      const isFromTrigger = edge.source === "start-trigger" || edge.source.startsWith("trigger-")
      return isFromTrigger && edge.selected
    })
    setSelectedEdges(triggerEdges.map(e => e.id))
  }, [edges])

  // Add keyboard shortcuts for copy/paste
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check if we're in an input field or textarea
      const isInputFocused = document.activeElement?.tagName === 'INPUT' || 
                            document.activeElement?.tagName === 'TEXTAREA' ||
                            document.activeElement?.contentEditable === 'true'
      
      if (isInputFocused) return

      // Ctrl+C or Cmd+C - Copy hovered or selected node
      if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
        event.preventDefault()
        
        // First try to find a selected node
        const selectedNodes = nodes.filter(node => node.selected)
        let targetNode = null
        
        if (selectedNodes.length === 1) {
          targetNode = selectedNodes[0]
        } else if (hoveredNodeIdRef.current) {
          // If no selected node, try to find the hovered node
          targetNode = nodes.find(node => node.id === hoveredNodeIdRef.current)
        }
        
        if (targetNode) {
          copyNode(targetNode.id, currentWorkflow?.id)
          toast({
            title: "Node Copied",
            description: `${targetNode.data?.name || targetNode.data?.label || 'Node'} has been copied to clipboard`,
          })
        } else {
          toast({
            title: "No Node to Copy",
            description: "Hover over a node and press Ctrl+C to copy it",
            variant: "destructive"
          })
        }
      }

      // Ctrl+V or Cmd+V - Paste node
      if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
        event.preventDefault()
        const canPaste = hasCopiedNode()
        if (canPaste) {
          const copiedInfo = getCopiedNodeInfo()
          if (copiedInfo) {
            // Calculate position for pasted node (center of viewport)
            const viewport = { x: 0, y: 0, zoom: 1 }
            const position = { x: 200, y: 100 } // Default position
            
            pasteNode(currentWorkflow?.id, position, csrf_token).then(pastedNode => {
              if (pastedNode) {
                const normalizedPastedNode = {
                  ...pastedNode,
                  data: {
                    ...(pastedNode.data || {}),
                    workflowId: currentWorkflow?.id,
                    databaseId: pastedNode?.data?.databaseId ?? pastedNode?.databaseId ?? null,
                    onOpenTriggersPanel: handleOpenTriggersPanel,
                    onOpenConnectionMenu: handleOpenConnectionMenu,
                    onNodeDataChange: handleNodeDataChange,
                  },
                }
                setNodes((nds) => {
                  if (nds.some((n) => n.id === normalizedPastedNode.id)) {
                    return nds
                  }
                  return [...nds, normalizedPastedNode]
                })
              }
              if (pastedNode) {
                toast({
                  title: "Node Pasted",
                  description: `${copiedInfo.nodeName} has been pasted`,
                })
              }
            }).catch(() => {})
          }
        } else {
          toast({
            title: "Nothing to Paste",
            description: "No node copied to clipboard",
            variant: "destructive"
          })
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [nodes, currentWorkflow, copyNode, pasteNode, hasCopiedNode, getCopiedNodeInfo, csrf_token, toast])

  // Node types registration - memoized to prevent recreation on every render
  const nodeTypes = useMemo(() => ({
    triggerNode: (props) => <TriggerNode {...props} csrf_token={csrf_token} />,
    actionNode: (props) => <ActionNode {...props} csrf_token={csrf_token} />,
    dataNode: (props) => <DataNode {...props} csrf_token={csrf_token} />,
    aiNode: (props) => <AINode {...props} csrf_token={csrf_token} />,
    responseNode: (props) => <ResponseNode {...props} csrf_token={csrf_token} />,
    placeholderNode: (props) => <PlaceholderNode {...props} csrf_token={csrf_token} />,
    workflowNode: (props) => <WorkflowNode {...props} csrf_token={csrf_token} />,
    branchNode: (props) => <BranchNode {...props} csrf_token={csrf_token} />,
    ifElseNode: (props) => <IfElseNode {...props} csrf_token={csrf_token} />,
    waitNode: (props) => <WaitNode {...props} csrf_token={csrf_token} />,
    taskNode: (props) => <TaskNode {...props} users={users || []} categories={categories || []} departments={departments || []} integrations={integrations || []} onChecklistInteraction={setIsChecklistInteracting} csrf_token={csrf_token} />,
    requestNode: (props) => <RequestNode {...props} csrf_token={csrf_token} />,
    formNode: (props) => <FormNode {...props} csrf_token={csrf_token} />,
    updateNoteNode: (props) => <UpdateNoteNode {...props} csrf_token={csrf_token} />,
    updateJobNode: (props) => <UpdateJobNode {...props} csrf_token={csrf_token} />,
    createNoteNode: (props) => <CreateNoteNode {...props} csrf_token={csrf_token} />,
    sendTextNode: (props) => <TextNode {...props} csrf_token={csrf_token} textTemplates={textTemplates || []} twilio={twilio || {}} />,
    sendEmailNode: (props) => <EmailNode {...props} csrf_token={csrf_token} emailTemplates={emailTemplates || []} emailAccounts={emailAccounts || []} envEmailDefault={envEmailDefault || null} />,
  }), [categories, departments, integrations, csrf_token, textTemplates, emailTemplates, emailAccounts, envEmailDefault, twilio])

  // Keep edgeTypes stable; React Flow warns if this object identity changes frequently.
  const edgeTypes = useMemo(() => {
    return {
      custom: (props) => (
        <CustomEdge 
          {...props} 
          csrf_token={csrf_token}
        />
      ),
    }
  }, [csrf_token])

  // Debug nodes changes
  // useEffect(() => {
  //   console.log("Nodes state changed, current count:", nodes.length)
  //   nodes.forEach((node, idx) => {
  //     console.log(`Node ${idx}:`, node.id, node.type, node.position)
  //   })
  // }, [nodes])

  // Add viewMode state - moved here to avoid temporal dead zone
  const [viewMode, setViewMode] = useState(currentWorkflow?.viewMode || "graph") // 'graph' (horizontal) or 'tree' (vertical)
  
  // Track if we've initialized nodes to prevent resets
  const [nodesInitialized, setNodesInitialized] = useState(false)
  
  // Track when we're actively adding nodes to prevent resets
  const [isAddingNode, setIsAddingNode] = useState(false)

  // ReactFlow instance state - must be declared before useEffects that use it
  const [reactFlowInstance, setReactFlowInstance] = useState(null)
  
  // State to track checklist interactions to disable panning
  const [isChecklistInteracting, setIsChecklistInteracting] = useState(false)
  
  // State for mock execution modal
  const [mockExecutionOpen, setMockExecutionOpen] = useState(false)
  
  // State for workflow history
  const [workflowHistory, setWorkflowHistory] = useState([])
  const [historyPagination, setHistoryPagination] = useState(null)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState(null)
  const [showHistoryPanel, setShowHistoryPanel] = useState(false)
  const [selectedEdges, setSelectedEdges] = useState([]) // Track selected edges for path execution

  // Function to save edge to database - defined early to avoid temporal dead zone
  const saveEdgeToDatabase = async (connection) => {
    // If no current workflow exists, try to create one
    let targetWorkflow = resolveTargetWorkflow()
    if (!targetWorkflow) {
      // Check if we're on a "new" workflow route
      if (workflowId === "new") {
        try {
          targetWorkflow = await createWorkflow("New Workflow", csrf_token)
        } catch (error) {
          console.error('Failed to create workflow for edge save:', error)
          return false
        }
      } else {
        // If we're not on a "new" route and no workflow exists, we can't proceed
        return false
      }
    }

    // Check if trigger nodes have active triggers (for validation display)
    const hasValidTriggers = connection.source === 'start-trigger' || connection.source.startsWith('trigger-') 
      ? hasActiveTriggers(connection.source)
      : true

    // Always save edges to database, even if triggers are inactive
    // This allows edges to persist and show validation state
    if (!hasValidTriggers) {
      // Show helpful warning message but still save the edge
      toast({
        title: "No active triggers",
        description: "Connection created but will be inactive until triggers are configured.",
        variant: "destructive",
      })
    }

    try {
      const csrfToken = csrf_token;

      // Parse node IDs to determine source and target types and IDs
      let sourceData = parseNodeId(connection.source)
      let targetData = parseNodeId(connection.target)

      // If either node hasn't been saved yet, wait a bit and try again
      if (!sourceData || !targetData) {
        console.log('One or both nodes not saved yet, waiting for save...', { source: connection.source, target: connection.target })
        
        // Wait for nodes to be saved (up to 3 seconds)
        for (let i = 0; i < 30; i++) {
          await new Promise(resolve => setTimeout(resolve, 100))
          sourceData = parseNodeId(connection.source)
          targetData = parseNodeId(connection.target)
          if (sourceData && targetData) {
            console.log('Nodes saved, proceeding with edge save')
            break
          }
        }
      }

      if (!sourceData || !targetData) {
        console.error('Unable to parse source or target node IDs after waiting:', connection.source, connection.target)
        return false
      }

      // Extract source_handle from connection.sourceHandle
      let sourceHandle = null;
      if (connection.sourceHandle && connection.sourceHandle.startsWith('output-')) {
        // For if/else nodes, extract the condition ID from the handle
        sourceHandle = connection.sourceHandle.replace('output-', '');
      }

      const requestBody = {
        source: sourceData.id,
        source_type: sourceData.type,
        target: targetData.id,
        target_type: targetData.type,
        source_handle: sourceHandle,
        name: `${sourceData.type}-${sourceData.id}-to-${targetData.type}-${targetData.id}`
      };

      const response = await fetch(`/workflows/${targetWorkflow.id}/edges`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
          'Accept': 'application/json',
        },
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        throw new Error(`Failed to save edge: ${response.status} - ${JSON.stringify(errorData)}`)
      }

      const savedEdge = await response.json()
      return savedEdge
    } catch (error) {
      console.error('Failed to save edge to database:', error)
      
      // Show user-friendly error message
      toast({
        title: "Connection failed",
        description: "Unable to create connection. Please try again.",
        variant: "destructive",
      })
      return false
    }
  }

  const createUpdateNoteNodeViaInertia = async (workflow, nodeObject) => {
    const stepData = {
      step_type: 'update_note',
      workflow_id: workflow.id,
      node_id: nodeObject.id,
      name: nodeObject.data?.name || 'Update Note',
      description: nodeObject.data?.description || '',
      position: JSON.stringify(nodeObject.position),
      settings: {
        operation: nodeObject.data?.operation ?? 'append',
        mode: nodeObject.data?.mode ?? 'job',
        recid: nodeObject.data?.recid ?? '',
        note_id: nodeObject.data?.noteId ?? '',
        note_notes: nodeObject.data?.noteText ?? '',
        category_source: nodeObject.data?.categorySource ?? 'label',
        note_category: nodeObject.data?.noteCategory ?? 'General',
        nct_id: nodeObject.data?.nctId ?? '',
        append_by: nodeObject.data?.appendBy ?? 'DME A/R Workflow',
        emp_id: nodeObject.data?.empId ?? '',
        note_important: Boolean(nodeObject.data?.noteImportant),
      },
      options: {
        expanded: nodeObject.data?.expanded ?? true,
        autoLayoutMode: nodeObject.data?.autoLayoutMode ?? false,
        handlePosition: nodeObject.data?.handlePosition ?? null,
        dynamicDateFormat: nodeObject.data?.dynamicDateFormat ?? DEFAULT_DYNAMIC_DATE_FORMAT,
      },
    }

    await new Promise((resolve, reject) => {
      router.post(`/workflows/${workflow.id}/steps`, stepData, {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => resolve(),
        onError: (errors) => reject(new Error(JSON.stringify(errors))),
      })
    })

    const graphResponse = await fetch(`/workflows/${workflow.id}/nodes-and-edges`, {
      headers: {
        'Accept': 'application/json',
      },
    })

    if (!graphResponse.ok) {
      throw new Error(`Failed to reload workflow graph after creating update_note node: ${graphResponse.status}`)
    }

    const graphData = await graphResponse.json()
    const createdNode = graphData?.nodes?.find((n) => n.id === nodeObject.id)

    if (!createdNode) {
      throw new Error('Unable to locate created update_note node in refreshed workflow graph')
    }

    return {
      ...nodeObject,
      id: createdNode.id,
      databaseId: createdNode.databaseId,
      data: {
        ...nodeObject.data,
        ...(createdNode.data || {}),
        databaseId: createdNode.databaseId,
        workflowId: workflow?.id,
      },
    }
  }

  const createUpdateJobNodeViaInertia = async (workflow, nodeObject) => {
    const stepData = {
      step_type: 'update_job',
      workflow_id: workflow.id,
      node_id: nodeObject.id,
      name: nodeObject.data?.name || 'Update Job',
      description: nodeObject.data?.description || '',
      position: JSON.stringify(nodeObject.position),
      settings: {
        operation: nodeObject.data?.operation ?? 'update_status',
        job_id: nodeObject.data?.jobId ?? '',
        jbs_id: nodeObject.data?.jbsId ?? '',
        job_status_label: nodeObject.data?.jobStatusLabel ?? '',
      },
      options: {
        expanded: nodeObject.data?.expanded ?? true,
        autoLayoutMode: nodeObject.data?.autoLayoutMode ?? false,
        handlePosition: nodeObject.data?.handlePosition ?? null,
        dynamicDateFormat: nodeObject.data?.dynamicDateFormat ?? DEFAULT_DYNAMIC_DATE_FORMAT,
      },
    }

    await new Promise((resolve, reject) => {
      router.post(`/workflows/${workflow.id}/steps`, stepData, {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => resolve(),
        onError: (errors) => reject(new Error(JSON.stringify(errors))),
      })
    })

    const graphResponse = await fetch(`/workflows/${workflow.id}/nodes-and-edges`, {
      headers: {
        'Accept': 'application/json',
      },
    })

    if (!graphResponse.ok) {
      throw new Error(`Failed to reload workflow graph after creating update_job node: ${graphResponse.status}`)
    }

    const graphData = await graphResponse.json()
    const createdNode = graphData?.nodes?.find((n) => n.id === nodeObject.id)

    if (!createdNode) {
      throw new Error('Unable to locate created update_job node in refreshed workflow graph')
    }

    return {
      ...nodeObject,
      id: createdNode.id,
      databaseId: createdNode.databaseId,
      data: {
        ...nodeObject.data,
        ...(createdNode.data || {}),
        databaseId: createdNode.databaseId,
        workflowId: workflow?.id,
      },
    }
  }

  // createNoteNode persists ALL of its CRUD via Inertia rather than going
  // through workflow-context. This helper uses a single Inertia POST request
  // (no follow-up fetch) and resolves the created step from Inertia's success
  // page props when available.
  const createCreateNoteNodeViaInertia = async (workflow, nodeObject) => {
    const stepData = {
      step_type: 'create_note',
      workflow_id: workflow.id,
      node_id: nodeObject.id,
      name: nodeObject.data?.name || 'Create Note',
      description: nodeObject.data?.description || '',
      position: JSON.stringify(nodeObject.position),
      settings: {
        mode: nodeObject.data?.mode ?? 'job',
        recid: nodeObject.data?.recid ?? '',
        note_notes: nodeObject.data?.noteText ?? '',
        category_source: nodeObject.data?.categorySource ?? 'label',
        note_category: nodeObject.data?.noteCategory ?? 'General',
        nct_id: nodeObject.data?.nctId ?? '',
        note_important: Boolean(nodeObject.data?.noteImportant),
      },
      options: {
        expanded: nodeObject.data?.expanded ?? true,
        autoLayoutMode: nodeObject.data?.autoLayoutMode ?? false,
        handlePosition: nodeObject.data?.handlePosition ?? null,
        dynamicDateFormat: nodeObject.data?.dynamicDateFormat ?? DEFAULT_DYNAMIC_DATE_FORMAT,
      },
    }

    const createdStep = await new Promise((resolve, reject) => {
      router.post(`/workflows/${workflow.id}/steps`, stepData, {
        preserveState: true,
        preserveScroll: true,
        onSuccess: (page) => {
          const steps = page?.props?.workflow?.steps
          const matchedStep = Array.isArray(steps)
            ? steps.find((step) => step?.node_id === nodeObject.id && step?.step_type === 'create_note')
            : null
          resolve(matchedStep || null)
        },
        onError: (errors) => reject(new Error(JSON.stringify(errors))),
      })
    })

    // Single-request flow: if Inertia page props include the new step, hydrate
    // databaseId + normalized node data. Otherwise keep optimistic node data so
    // the node is still rendered immediately.
    const resolvedDatabaseId = createdStep?.id ?? nodeObject?.databaseId ?? null

    return {
      ...nodeObject,
      id: nodeObject.id,
      databaseId: resolvedDatabaseId,
      data: {
        ...nodeObject.data,
        name: createdStep?.name ?? nodeObject.data?.name ?? 'Create Note',
        description: createdStep?.description ?? nodeObject.data?.description ?? '',
        mode: createdStep?.settings?.mode ?? nodeObject.data?.mode ?? 'job',
        recid: createdStep?.settings?.recid ?? nodeObject.data?.recid ?? '',
        noteText: createdStep?.settings?.note_notes ?? nodeObject.data?.noteText ?? '',
        categorySource: createdStep?.settings?.category_source ?? nodeObject.data?.categorySource ?? 'label',
        noteCategory: createdStep?.settings?.note_category ?? nodeObject.data?.noteCategory ?? 'General',
        nctId: createdStep?.settings?.nct_id ?? nodeObject.data?.nctId ?? '',
        noteImportant: Boolean(createdStep?.settings?.note_important ?? nodeObject.data?.noteImportant),
        databaseId: resolvedDatabaseId,
      },
    }
  }

  const createSendTextNodeViaInertia = async (workflow, nodeObject) => {
    const stepData = {
      step_type: 'send_text',
      workflow_id: workflow.id,
      node_id: nodeObject.id,
      name: nodeObject.data?.name || 'Send SMS',
      description: nodeObject.data?.description || '',
      position: JSON.stringify(nodeObject.position),
      settings: {
        template_id: nodeObject.data?.templateId ?? '',
        recipient_source: nodeObject.data?.recipientSource ?? 'variable',
        recipient_phone: nodeObject.data?.recipientPhone ?? '',
        variable_mappings: nodeObject.data?.variableMappings ?? {},
        message_override: nodeObject.data?.messageOverride ?? '',
        use_template_message: nodeObject.data?.useTemplateMessage ?? true,
        media_urls: nodeObject.data?.mediaUrls ?? [],
        media_source: nodeObject.data?.mediaSource ?? 'both',
      },
      options: {
        expanded: nodeObject.data?.expanded ?? true,
        autoLayoutMode: nodeObject.data?.autoLayoutMode ?? false,
        handlePosition: nodeObject.data?.handlePosition ?? null,
        dynamicDateFormat: nodeObject.data?.dynamicDateFormat ?? DEFAULT_DYNAMIC_DATE_FORMAT,
      },
    }

    const createdStep = await new Promise((resolve, reject) => {
      router.post(`/workflows/${workflow.id}/steps`, stepData, {
        preserveState: true,
        preserveScroll: true,
        onSuccess: (page) => {
          const steps = page?.props?.workflow?.steps
          const matchedStep = Array.isArray(steps)
            ? steps.find((step) => step?.node_id === nodeObject.id && step?.step_type === 'send_text')
            : null
          resolve(matchedStep || null)
        },
        onError: (errors) => reject(new Error(JSON.stringify(errors))),
      })
    })

    const resolvedDatabaseId = createdStep?.id ?? nodeObject?.databaseId ?? null
    const settings = createdStep?.settings ?? stepData.settings

    return {
      ...nodeObject,
      id: nodeObject.id,
      databaseId: resolvedDatabaseId,
      data: {
        ...nodeObject.data,
        name: createdStep?.name ?? nodeObject.data?.name ?? 'Send SMS',
        description: createdStep?.description ?? nodeObject.data?.description ?? '',
        templateId: settings?.template_id ?? nodeObject.data?.templateId ?? '',
        recipientSource: settings?.recipient_source ?? nodeObject.data?.recipientSource ?? 'variable',
        recipientPhone: settings?.recipient_phone ?? nodeObject.data?.recipientPhone ?? '',
        variableMappings: settings?.variable_mappings ?? nodeObject.data?.variableMappings ?? {},
        messageOverride: settings?.message_override ?? nodeObject.data?.messageOverride ?? '',
        useTemplateMessage: settings?.use_template_message ?? nodeObject.data?.useTemplateMessage ?? true,
        mediaUrls: settings?.media_urls ?? nodeObject.data?.mediaUrls ?? [],
        mediaSource: settings?.media_source ?? nodeObject.data?.mediaSource ?? 'both',
        databaseId: resolvedDatabaseId,
      },
    }
  }

  const createSendEmailNodeViaInertia = async (workflow, nodeObject) => {
    const stepData = {
      step_type: 'send_email',
      workflow_id: workflow.id,
      node_id: nodeObject.id,
      name: nodeObject.data?.name || 'Send Email',
      description: nodeObject.data?.description || '',
      position: JSON.stringify(nodeObject.position),
      settings: {
        template_id: nodeObject.data?.templateId ?? '',
        email_account_id: nodeObject.data?.emailAccountId ?? '',
        recipient_source: nodeObject.data?.recipientSource ?? 'variable',
        recipient_email: nodeObject.data?.recipientEmail ?? '',
        variable_mappings: nodeObject.data?.variableMappings ?? {},
        message_override: nodeObject.data?.messageOverride ?? '',
        subject_override: nodeObject.data?.subjectOverride ?? '',
        use_template_message: nodeObject.data?.useTemplateMessage ?? true,
        use_template_subject: nodeObject.data?.useTemplateSubject ?? true,
      },
      options: {
        expanded: nodeObject.data?.expanded ?? true,
        autoLayoutMode: nodeObject.data?.autoLayoutMode ?? false,
        handlePosition: nodeObject.data?.handlePosition ?? null,
        dynamicDateFormat: nodeObject.data?.dynamicDateFormat ?? DEFAULT_DYNAMIC_DATE_FORMAT,
      },
    }

    const createdStep = await new Promise((resolve, reject) => {
      router.post(`/workflows/${workflow.id}/steps`, stepData, {
        preserveState: true,
        preserveScroll: true,
        onSuccess: (page) => {
          const steps = page?.props?.workflow?.steps
          const matchedStep = Array.isArray(steps)
            ? steps.find((step) => step?.node_id === nodeObject.id && step?.step_type === 'send_email')
            : null
          resolve(matchedStep || null)
        },
        onError: (errors) => reject(new Error(JSON.stringify(errors))),
      })
    })

    const resolvedDatabaseId = createdStep?.id ?? nodeObject?.databaseId ?? null
    const settings = createdStep?.settings ?? stepData.settings

    return {
      ...nodeObject,
      id: nodeObject.id,
      databaseId: resolvedDatabaseId,
      data: {
        ...nodeObject.data,
        name: createdStep?.name ?? nodeObject.data?.name ?? 'Send Email',
        description: createdStep?.description ?? nodeObject.data?.description ?? '',
        templateId: settings?.template_id ?? nodeObject.data?.templateId ?? '',
        emailAccountId: settings?.email_account_id ?? nodeObject.data?.emailAccountId ?? '',
        recipientSource: settings?.recipient_source ?? nodeObject.data?.recipientSource ?? 'variable',
        recipientEmail: settings?.recipient_email ?? nodeObject.data?.recipientEmail ?? '',
        variableMappings: settings?.variable_mappings ?? nodeObject.data?.variableMappings ?? {},
        messageOverride: settings?.message_override ?? nodeObject.data?.messageOverride ?? '',
        subjectOverride: settings?.subject_override ?? nodeObject.data?.subjectOverride ?? '',
        useTemplateMessage: settings?.use_template_message ?? nodeObject.data?.useTemplateMessage ?? true,
        useTemplateSubject: settings?.use_template_subject ?? nodeObject.data?.useTemplateSubject ?? true,
        databaseId: resolvedDatabaseId,
      },
    }
  }

  const resolveNodeDatabaseId = (node) => {
    if (!node) return null
    return node.databaseId ?? node.data?.databaseId ?? null
  }

  // Helper function to parse node IDs and extract type and database ID
  const parseNodeId = (nodeId) => {
    if (nodeId === 'start-trigger') {
      return {
        id: 'start-trigger',
        type: 'trigger',
      }
    }

    if (nodeId.startsWith('trigger-')) {
      // Send the full canvas node id; backend resolves by node_id or numeric id.
      return {
        id: nodeId,
        type: 'trigger',
      }
    }

    const numericStepMatch = nodeId.match(/^step-(\d+)$/)
    if (numericStepMatch) {
      return {
        id: numericStepMatch[1],
        type: 'step',
      }
    }

    if (
      nodeId.startsWith('requestNode-') || nodeId.startsWith('taskNode-') || nodeId.startsWith('formNode-') || nodeId.startsWith('waitNode-') ||
      nodeId.startsWith('actionNode-') || nodeId.startsWith('dataNode-') || nodeId.startsWith('aiNode-') ||
      nodeId.startsWith('responseNode-') || nodeId.startsWith('workflowNode-') || nodeId.startsWith('branchNode-') ||
      nodeId.startsWith('ifElseNode-') || nodeId.startsWith('placeholderNode-') || nodeId.startsWith('updateNoteNode-') || nodeId.startsWith('updateJobNode-') || nodeId.startsWith('createNoteNode-') || nodeId.startsWith('sendTextNode-') || nodeId.startsWith('sendEmailNode-') ||
      nodeId.startsWith('step-')
    ) {
      const node = nodes.find((n) => n.id === nodeId)
      const databaseId = resolveNodeDatabaseId(node)

      if (databaseId) {
        return {
          id: databaseId.toString(),
          type: 'step',
        }
      }

      if (node) {
        return {
          id: nodeId,
          type: 'step',
        }
      }

      console.warn(`Node ${nodeId} hasn't been saved to database yet, skipping edge save`)
      return null
    }

    return null
  }

  // Update nodes and edges when currentWorkflow changes or when workflow prop is available
  useEffect(() => {
    // Only initialize nodes if they haven't been initialized yet, and we're not currently adding a node
    if (nodesInitialized || isAddingNode) {
      return
    }
    
    if (workflow) {
      // Use workflow data from controller
      setWorkflowName(workflow.name)
      
      // Convert workflow steps, triggers, and edges to ReactFlow format
      const initialNodes = []
      
      // Always add a trigger node if none exists
      let hasTriggerNode = false
      
      // Group triggers by node_id and create trigger nodes
      if (workflow.triggers && workflow.triggers.length > 0) {
        const triggersByNodeId = {}
        
        // Group triggers by node_id
        workflow.triggers.forEach(trigger => {
          const nodeId = trigger.node_id || `trigger-${trigger.id}`
          if (!triggersByNodeId[nodeId]) {
            triggersByNodeId[nodeId] = {
              nodeId: nodeId,
              position: trigger.position || { x: 100, y: 100 },
              triggers: []
            }
          }
          triggersByNodeId[nodeId].triggers.push(trigger)
        })
        
        // Create one trigger node per unique node_id
        Object.values(triggersByNodeId).forEach(nodeGroup => {
          hasTriggerNode = true
          
          // Convert database triggers to enabledTriggers format
          const enabledTriggers = nodeGroup.triggers
            .filter(trigger => trigger.enabled)
            .map(trigger => ({
              id: `${trigger.trigger_type}-${trigger.id}`,
              type: trigger.trigger_type,
              name: trigger.name || trigger.trigger_type,
              description: trigger.description || '',
              enabled: trigger.enabled,
              config: trigger.options || {}
            }))
          

          
          // Use the first trigger for node-level properties
          const firstTrigger = nodeGroup.triggers[0]
          
          initialNodes.push({
            id: nodeGroup.nodeId,
            type: 'triggerNode',
            position: nodeGroup.position,
            data: {
              label: firstTrigger.name || 'startTrigger',
              triggerType: firstTrigger.trigger_type,
              description: firstTrigger.description,
              enabledTriggers: enabledTriggers,
              needsConfiguration: enabledTriggers.length === 0, // Flag to indicate if this node needs trigger configuration
              // Include options data for auto layout support
              expanded: firstTrigger.options?.expanded ?? true,
              autoLayoutMode: firstTrigger.options?.autoLayoutMode ?? false,
              handlePosition: firstTrigger.options?.handlePosition ?? null,
              workflowId: workflowId || workflow?.id,
              onOpenTriggersPanel: handleOpenTriggersPanel,
              onNodeDataChange: handleNodeDataChange,
            }
          })
        })
      }
      
      // Add default trigger node if none exists (for existing workflows created before auto-trigger feature)
      if (!hasTriggerNode) {
        // Create default trigger node that will load triggers from database or be empty
        initialNodes.push({
          id: 'start-trigger',
          type: 'triggerNode',
          position: { x: 100, y: 100 },
          data: {
            label: 'startTrigger',
            triggerType: 'schedule',
            enabledTriggers: [], // Will be populated by TriggerNode component from database
            needsConfiguration: true, // Flag to indicate this node needs trigger configuration
            // Include auto layout support (defaults for new workflows)
            expanded: true,
            autoLayoutMode: false,
            handlePosition: null,
            workflowId: workflowId || workflow?.id,
            onOpenTriggersPanel: handleOpenTriggersPanel,
            onNodeDataChange: handleNodeDataChange,
          }
        })
      }
      
      // Add steps as nodes
      if (workflow.steps) {
        workflow.steps.forEach(step => {
          let nodeType = 'taskNode';
          let nodeData = {
            label: step.name,
            name: step.name,
            description: step.description,
            link: step.link || "",
            link_title: step.link_title || "",
            category: step.category,
            priority: step.priority,
            due: step.due,
            status: step.status,
            deferrable: step.deferrable,
            missable: step.missable,
            taskTypeCategories: step.task_type || [],
            assignee_id: step.assignee_id?.toString() || "",
            assignee: step.assignee || null,
            // Include assignment for task nodes (default shared: true so multi-assignee steps create one shared task)
            assignment: step.assignment || { type: 'individual', user_ids: [], department_ids: [], shared: true },
            // Include settings for task nodes
            settings: step.settings || {},
            // Include checklist for task nodes
            checklist: step.checklist || [],
            // Include options data for auto layout support
            expanded: step.options?.expanded ?? true,
            autoLayoutMode: step.options?.autoLayoutMode ?? false,
            handlePosition: step.options?.handlePosition ?? null,
            dynamicDateFormat: step.options?.dynamicDateFormat ?? DEFAULT_DYNAMIC_DATE_FORMAT,
            onNodeDataChange: handleNodeDataChange,
            onDeleteNode: (nodeId) => {
              setNodes((nds) => nds.filter((n) => n.id !== nodeId))
              setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
            },
            workflowId: workflowId || workflow?.id,
            databaseId: step.id,
          };

          // If this is a request step, use requestNode and map request fields
          if (step.step_type === 'request') {
            nodeType = 'requestNode';
            nodeData = {
              label: step.name,
              name: step.name,
              description: step.description,
              url: step.url,
              method: step.method,
              headers: step.headers,
              body: step.body,
              response: step.response,
              extractedVariables: step.extracted_variables,
              expanded: step.options?.expanded ?? true,
              autoLayoutMode: step.options?.autoLayoutMode ?? false,
              handlePosition: step.options?.handlePosition ?? null,
              dynamicDateFormat: step.options?.dynamicDateFormat ?? DEFAULT_DYNAMIC_DATE_FORMAT,
              onNodeDataChange: handleNodeDataChange,
            };
          }

          // If this is a form step, use formNode and map form fields
          if (step.step_type === 'form') {
            nodeType = 'formNode';
            nodeData = {
              label: step.name,
              name: step.name,
              description: step.description,
              fields: step.fields || [],
              settings: step.settings || {},
              expanded: step.options?.expanded ?? true,
              autoLayoutMode: step.options?.autoLayoutMode ?? false,
              handlePosition: step.options?.handlePosition ?? null,
              onNodeDataChange: handleNodeDataChange,
            };
          }

          // If this is a wait step, use waitNode and map wait fields
          if (step.step_type === 'wait') {
            nodeType = 'waitNode';
            nodeData = {
              label: step.name || 'Wait',
              name: step.name || 'Wait',
              description: step.description || '',
              durationUnit: step.wait_duration_unit || 'days',
              durationExpression: step.wait_duration_expression || '1',
              targetTime: step.wait_target_time || '08:00',
              wait_duration_unit: step.wait_duration_unit || 'days',
              wait_duration_expression: step.wait_duration_expression || '1',
              wait_target_time: step.wait_target_time || '08:00',
              expanded: step.options?.expanded ?? true,
              autoLayoutMode: step.options?.autoLayoutMode ?? false,
              handlePosition: step.options?.handlePosition ?? null,
              onNodeDataChange: handleNodeDataChange,
            };
          }

          if (step.step_type === 'action') {
            nodeType = 'actionNode';
            const actionSettings = step.settings || {};
            nodeData = {
              label: step.name || 'Set Suppression',
              name: step.name || 'Set Suppression',
              description: step.description || '',
              settings: actionSettings,
              operation: actionSettings.operation || 'set_suppression',
              entityType: actionSettings.entity_type || 'contract',
              entityKeySource: actionSettings.entity_key_source || 'variable',
              entityKeyVariable: actionSettings.entity_key_variable || 'contractid',
              suppressionType: actionSettings.suppression_type || 'workflow_task_creation',
              reasonCode: actionSettings.reason_code || 'wait_full_project_completion',
              reasonText: actionSettings.reason_text || '',
              scopeType: actionSettings.scope_type || 'workflow',
              expanded: step.options?.expanded ?? true,
              autoLayoutMode: step.options?.autoLayoutMode ?? false,
              handlePosition: step.options?.handlePosition ?? null,
              onNodeDataChange: handleNodeDataChange,
            };
          }

          if (step.step_type === 'if_else') {
            nodeType = 'ifElseNode';
            const ifElseSettings = step.settings || {};
            nodeData = {
              label: step.name || 'If/Else',
              name: step.name || 'If/Else',
              description: step.description || '',
              conditions: Array.isArray(step.conditions) ? step.conditions : [],
              settings: ifElseSettings,
              expanded: step.options?.expanded ?? true,
              autoLayoutMode: step.options?.autoLayoutMode ?? false,
              handlePosition: step.options?.handlePosition ?? null,
              workflowId: workflowId || workflow?.id,
              databaseId: step.id,
              onNodeDataChange: handleNodeDataChange,
              onDeleteNode: (nodeId) => {
                setNodes((nds) => nds.filter((n) => n.id !== nodeId))
                setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
              },
            };
          }

          if (step.step_type === 'update_note') {
            nodeType = 'updateNoteNode';
            const updateSettings = step.settings || {};
            nodeData = {
              label: step.name || 'Update Note',
              name: step.name || 'Update Note',
              description: step.description || '',
              settings: updateSettings,
              operation: updateSettings.operation || 'append',
              mode: updateSettings.mode || 'job',
              recid: updateSettings.recid || '',
              noteId: updateSettings.note_id || '',
              noteText: updateSettings.note_notes || '',
              categorySource: updateSettings.category_source || 'label',
              noteCategory: updateSettings.note_category || 'General',
              nctId: updateSettings.nct_id || '',
              appendBy: updateSettings.append_by || 'DME A/R Workflow',
              empId: updateSettings.emp_id || '',
              noteImportant: Boolean(updateSettings.note_important),
              workflowId: workflowId || workflow?.id,
              databaseId: step.id,
              expanded: step.options?.expanded ?? true,
              autoLayoutMode: step.options?.autoLayoutMode ?? false,
              handlePosition: step.options?.handlePosition ?? null,
              dynamicDateFormat: step.options?.dynamicDateFormat ?? DEFAULT_DYNAMIC_DATE_FORMAT,
              onNodeDataChange: handleNodeDataChange,
              onDeleteNode: (nodeId) => {
                setNodes((nds) => nds.filter((n) => n.id !== nodeId))
                setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
              },
            };
          }

          if (step.step_type === 'update_job') {
            nodeType = 'updateJobNode';
            const updateJobSettings = step.settings || {};
            nodeData = {
              label: step.name || 'Update Job',
              name: step.name || 'Update Job',
              description: step.description || '',
              settings: updateJobSettings,
              operation: updateJobSettings.operation || 'update_status',
              jobId: updateJobSettings.job_id || '',
              jbsId: updateJobSettings.jbs_id || '',
              jobStatusLabel: updateJobSettings.job_status_label || '',
              workflowId: workflowId || workflow?.id,
              databaseId: step.id,
              expanded: step.options?.expanded ?? true,
              autoLayoutMode: step.options?.autoLayoutMode ?? false,
              handlePosition: step.options?.handlePosition ?? null,
              dynamicDateFormat: step.options?.dynamicDateFormat ?? DEFAULT_DYNAMIC_DATE_FORMAT,
              onDeleteNode: (nodeId) => {
                setNodes((nds) => nds.filter((n) => n.id !== nodeId))
                setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
              },
              onNodeDataChange: handleNodeDataChange,
            };
          }

          if (step.step_type === 'create_note') {
            nodeType = 'createNoteNode';
            const createSettings = step.settings || {};
            nodeData = {
              label: step.name || 'Create Note',
              name: step.name || 'Create Note',
              description: step.description || '',
              settings: createSettings,
              mode: createSettings.mode || 'job',
              recid: createSettings.recid || '',
              noteText: createSettings.note_notes || '',
              categorySource: createSettings.category_source || 'label',
              noteCategory: createSettings.note_category || 'General',
              nctId: createSettings.nct_id || '',
              noteImportant: Boolean(createSettings.note_important),
              workflowId: workflowId || workflow?.id,
              databaseId: step.id,
              expanded: step.options?.expanded ?? true,
              autoLayoutMode: step.options?.autoLayoutMode ?? false,
              handlePosition: step.options?.handlePosition ?? null,
              dynamicDateFormat: step.options?.dynamicDateFormat ?? DEFAULT_DYNAMIC_DATE_FORMAT,
              onDeleteNode: (nodeId) => {
                setNodes((nds) => nds.filter((n) => n.id !== nodeId))
                setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
              },
              onNodeDataChange: handleNodeDataChange,
            };
          }

          if (step.step_type === 'send_text') {
            nodeType = 'sendTextNode';
            const textSettings = step.settings || {};
            nodeData = {
              label: step.name || 'Send SMS',
              name: step.name || 'Send SMS',
              description: step.description || '',
              settings: textSettings,
              templateId: textSettings.template_id || '',
              recipientSource: textSettings.recipient_source || 'variable',
              recipientPhone: textSettings.recipient_phone || '',
              variableMappings: textSettings.variable_mappings || {},
              messageOverride: textSettings.message_override || '',
              useTemplateMessage: textSettings.use_template_message ?? true,
              mediaUrls: textSettings.media_urls || [],
              mediaSource: textSettings.media_source || 'both',
              workflowId: workflowId || workflow?.id,
              databaseId: step.id,
              expanded: step.options?.expanded ?? true,
              autoLayoutMode: step.options?.autoLayoutMode ?? false,
              handlePosition: step.options?.handlePosition ?? null,
              dynamicDateFormat: step.options?.dynamicDateFormat ?? DEFAULT_DYNAMIC_DATE_FORMAT,
              onDeleteNode: (nodeId) => {
                setNodes((nds) => nds.filter((n) => n.id !== nodeId))
                setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
              },
              onNodeDataChange: handleNodeDataChange,
            };
          }

          if (step.step_type === 'send_email') {
            nodeType = 'sendEmailNode';
            const emailSettings = step.settings || {};
            nodeData = {
              label: step.name || 'Send Email',
              name: step.name || 'Send Email',
              description: step.description || '',
              settings: emailSettings,
              templateId: emailSettings.template_id || '',
              emailAccountId: emailSettings.email_account_id || '',
              recipientSource: emailSettings.recipient_source || 'variable',
              recipientEmail: emailSettings.recipient_email || '',
              variableMappings: emailSettings.variable_mappings || {},
              messageOverride: emailSettings.message_override || '',
              subjectOverride: emailSettings.subject_override || '',
              useTemplateMessage: emailSettings.use_template_message ?? true,
              useTemplateSubject: emailSettings.use_template_subject ?? true,
              workflowId: workflowId || workflow?.id,
              databaseId: step.id,
              expanded: step.options?.expanded ?? true,
              autoLayoutMode: step.options?.autoLayoutMode ?? false,
              handlePosition: step.options?.handlePosition ?? null,
              dynamicDateFormat: step.options?.dynamicDateFormat ?? DEFAULT_DYNAMIC_DATE_FORMAT,
              onDeleteNode: (nodeId) => {
                setNodes((nds) => nds.filter((n) => n.id !== nodeId))
                setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
              },
              onNodeDataChange: handleNodeDataChange,
            };
          }

          initialNodes.push({
            id: `step-${step.id}`,
            type: nodeType,
            position: step.position || { x: 300, y: 100 },
            databaseId: step.id,
            data: nodeData,
          });
        })
      }
      
      // Convert edges to ReactFlow format
      const initialEdges = []
      if (workflow.edges) {
        workflow.edges.forEach(edge => {
          // Handle source node ID
          let sourceId
          if (edge.sourceable_type === 'App\\Models\\WorkflowTrigger') {
            // For triggers, check if it has a node_id (like start-trigger) or use trigger-{id}
            if (edge.sourceable && edge.sourceable.node_id) {
              sourceId = edge.sourceable.node_id
            } else {
              sourceId = `trigger-${edge.sourceable_id}`
            }
          } else {
            sourceId = `step-${edge.sourceable_id}`
          }

          // Handle target node ID  
          let targetId
          if (edge.targetable_type === 'App\\Models\\WorkflowTrigger') {
            // For triggers, check if it has a node_id (like start-trigger) or use trigger-{id}
            if (edge.targetable && edge.targetable.node_id) {
              targetId = edge.targetable.node_id
            } else {
              targetId = `trigger-${edge.targetable_id}`
            }
          } else {
            targetId = `step-${edge.targetable_id}`
          }

          const reactFlowEdge = {
            id: `edge-${edge.id}`,
            source: sourceId,
            target: targetId,
            type: 'custom',
            sourceHandle:
              edge.sourceHandle ??
              (edge.source_handle ? `output-${edge.source_handle}` : 'a'),
            targetHandle: edge.targetHandle ?? 'b',
            data: { 
              nodes, // Pass nodes data for edge validation
              workflowId: workflowId || workflow?.id // Pass workflow ID for edge deletion
            }
          }
          initialEdges.push(reactFlowEdge)
        })
      }
      
      setNodes(initialNodes)
      setEdges(initialEdges)
      setNodesInitialized(true)

      const resolvedWorkflowId = (workflowId || workflow?.id)?.toString()
      if (resolvedWorkflowId && initialNodes.length > 0) {
        updateWorkflow(
          resolvedWorkflowId,
          { nodes: initialNodes, edges: initialEdges },
          true,
        )
      }
    } else if (currentWorkflow) {
      // Fallback to workflow context data
      setWorkflowName(currentWorkflow.name)
      
      // Initialize nodes with proper data structure
      const initialNodes = currentWorkflow.nodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          workflowId: currentWorkflowId, // Add workflowId to node data
        }
      }))
      
      // Set nodes and edges
      setNodes(initialNodes)
      const hydratedEdges = currentWorkflow.edges
        .filter(edge => !edge.id.startsWith('temp-edge-')) // Remove any temporary edges
        .reduce((acc, edge) => {
          // Check if this edge already exists to prevent duplicates
          const existingEdge = acc.find(e => 
            e.source === edge.source &&
            e.target === edge.target &&
            e.sourceHandle === edge.sourceHandle &&
            e.targetHandle === edge.targetHandle
          )
          
          if (existingEdge) {
            // Keep the edge with the database ID (edge-{id} format)
            if (edge.id.startsWith('edge-') && !existingEdge.id.startsWith('edge-')) {
              return acc.map(e =>
                e.source === edge.source &&
                e.target === edge.target &&
                e.sourceHandle === edge.sourceHandle &&
                e.targetHandle === edge.targetHandle
                  ? edge
                  : e
              )
            }
            return acc
          }
          
          return [...acc, edge]
        }, [])
        .map(edge => ({
          ...edge,
          data: {
            ...edge.data,
            workflowId: currentWorkflow.id
          }
        }))
      setEdges(hydratedEdges)
      setNodesInitialized(true)

      if (currentWorkflow.id && initialNodes.length > 0) {
        updateWorkflow(
          currentWorkflow.id,
          { nodes: initialNodes, edges: hydratedEdges },
          true,
        )
      }
    }
  }, [workflow, workflowId, nodesInitialized]) // Remove currentWorkflow from dependencies to prevent reset on node addition

  // Sync local nodes state with workflow context changes (for deletions, etc.)
  useEffect(() => {
    // In server-backed builder pages, local canvas state is primarily driven
    // by the `workflow` prop from Inertia responses. If we also sync from
    // workflow-context here, newly created Inertia-persisted nodes (like
    // createNoteNode) can be overwritten before they are shown.
    if (workflow) {
      return
    }

    if (currentWorkflow && nodesInitialized && !isAddingNode) {
      // Update local nodes state to match workflow context
      setNodes(currentWorkflow.nodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          workflowId: currentWorkflow.id,
          onOpenTriggersPanel: handleOpenTriggersPanel,
          onOpenConnectionMenu: handleOpenConnectionMenu,
          onNodeDataChange: handleNodeDataChange,
        }
      })))
      
      // Update local edges state to match workflow context, removing any temporary edges and duplicates
      setEdges(currentWorkflow.edges)
    }
  }, [workflow, currentWorkflow, nodesInitialized, isAddingNode]) // Remove currentWorkflow from dependencies to prevent reset on node addition

  // Fit view to show all nodes when workflow loads
  useEffect(() => {
    const nonTriggerNodes = nodes.filter(node => node.type !== 'triggerNode')
    if (nodesInitialized && reactFlowInstance && nonTriggerNodes.length < 1) {
      // Use setTimeout to ensure nodes are fully rendered before fitting
      setTimeout(() => {
        reactFlowInstance.fitView({ 
          padding: 0.5,
          includeHiddenNodes: false,
          maxZoom: FIT_VIEW_MAX_ZOOM,
        })
      }, 100)
    } else if (nodesInitialized && reactFlowInstance && nonTriggerNodes.length >= 1) {
      // Use setTimeout to ensure nodes are fully rendered before fitting
      setTimeout(() => {
        reactFlowInstance.fitView({ 
          padding: 0.25,
          includeHiddenNodes: false,
          maxZoom: FIT_VIEW_MAX_ZOOM,
        })
      }, 100)
    }
  }, [nodesInitialized, reactFlowInstance])

  // Update viewMode when currentWorkflow changes
  useEffect(() => {
    if (currentWorkflow?.viewMode) {
      setViewMode(currentWorkflow.viewMode)
    }
  }, [currentWorkflow])

  // Add a new state to track enabled triggers
  const [enabledTriggers, setEnabledTriggers] = useState([])

  // Editable workflow name state
  const [showDeployModal, setShowDeployModal] = useState(false)

  // Deploy popover open state
  const [deployPopoverOpen, setDeployPopoverOpen] = useState(false)

  // Set workflow name if it exists in mock data
  const [currentTime, setCurrentTime] = useState("")
  const [showDeployDialog, setShowDeployDialog] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  // Panel open state for sidebar highlight and open/close
  const [openPanel, setOpenPanel] = useState(null)
  const [showBlocksPanel, setShowBlocksPanel] = useState(false)
  const [showTriggersPanel, setShowTriggersPanel] = useState(false)
  const [triggersContext, setTriggersContext] = useState({ workflowId: null, nodeId: null, enabledTriggers: [] })
  const [showOutlinePanel, setShowOutlinePanel] = useState(false)

  useEffect(() => {
    if (currentWorkflow) {
      setWorkflowName(currentWorkflow.name)
    }
  }, [currentWorkflow])

  const reactFlowWrapper = useRef(null)
  const [activeTrigger, setActiveTrigger] = useState(null)
  const [showConnectionMenu, setShowConnectionMenu] = useState(false)
  const [connectionMenuPosition, setConnectionMenuPosition] = useState({
    x: 0,
    y: 0,
  })
  const [sourceNodeId, setSourceNodeId] = useState(null)
  const [isInputConnection, setIsInputConnection] = useState(false)
  const [dropPosition, setDropPosition] = useState(null)
  const [placeholderNodeId, setPlaceholderNodeId] = useState(null)
  const [temporaryEdgeId, setTemporaryEdgeId] = useState(null)
  const isDragging = useRef(false)
  const menuNodeId = useRef(null)
  const [showMiniMap, setShowMiniMap] = useState(false)
  const [showMapButton, setShowMapButton] = useState(true)

  // Set initial zoom level - smaller for better visual effect
  const [initialViewport, setInitialViewport] = useState({
    x: 0,
    y: 0,
    zoom: 0.5, // Set to 50% for better visual effect when zooming into nodes
  })

  // Store the source node ID for freeform connections
  const [freeformSourceNodeId, setFreeformSourceNodeId] = useState(null)

  // Helper to get node under mouse position
  const getNodeAtPosition = (x, y) => {
    const rfPane = document.querySelector(".react-flow__pane")
    if (!rfPane) return null
    const rect = rfPane.getBoundingClientRect()
    const relX = x - rect.left
    const relY = y - rect.top
    // Find node whose bounding box contains the point
    for (const node of nodes) {
      const nodeEl = document.querySelector(`[data-id="${node.id}"]`)
      if (nodeEl) {
        const nodeRect = nodeEl.getBoundingClientRect()
        if (x >= nodeRect.left && x <= nodeRect.right && y >= nodeRect.top && y <= nodeRect.bottom) {
          return node
        }
      }
    }
    return null
  }



  // Freeform connect: start
  const onNodeMouseDown = (event, node) => {
    // Skip freeform connections for ifElseNode - let it use standard ReactFlow handle connections
    if (node.type === "ifElseNode") {
      console.log('Skipping freeform connection for ifElseNode - use standard handle connections')
      return
    }
    
    setFreeformSourceNodeId(node.id)
    document.body.style.cursor = "crosshair"
    window.addEventListener("mouseup", onFreeformConnectEnd)
  }

  // Freeform connect: end
  const onFreeformConnectEnd = useCallback((event) => {
    document.body.style.cursor = ""
    window.removeEventListener("mouseup", onFreeformConnectEnd)
    if (!freeformSourceNodeId) return
    const targetNode = getNodeAtPosition(event.clientX, event.clientY)
    if (targetNode && targetNode.id !== freeformSourceNodeId) {
      // Check if this connection already exists to prevent duplicates
      const existingEdge = edges.find(edge => 
        edge.source === freeformSourceNodeId && 
        edge.target === targetNode.id
      )
      
      if (existingEdge) {
        console.log('Freeform connection already exists, skipping:', { source: freeformSourceNodeId, target: targetNode.id })
        setFreeformSourceNodeId(null)
        return
      }

      // Create a unique temporary ID for the edge
      const tempEdgeId = `temp-edge-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      
      const newEdge = {
        id: tempEdgeId,
        source: freeformSourceNodeId,
        target: targetNode.id,
        sourceHandle: "a",
        targetHandle: "b",
        type: "custom",
        data: { 
          nodes, // Pass nodes data for edge validation
          workflowId: currentWorkflow?.id // Pass workflow ID for edge deletion
        }
      }

      // Add edge to workflow context first
      if (currentWorkflow) {
        addEdge(currentWorkflow.id, newEdge, csrf_token)
      }
      
      // Update local state
      setEdges((eds) => {
        // Check if this edge already exists to prevent duplicates
        const existingEdge = eds.find(edge => 
          edge.source === newEdge.source && 
          edge.target === newEdge.target
        )
        
        if (existingEdge) {
          console.log('Edge already exists in local state, skipping:', newEdge)
          return eds
        }
        
        return [...eds, newEdge]
      })

      // Save edge to database
      if (currentWorkflow) {
        saveEdgeToDatabase({
          source: freeformSourceNodeId,
          target: targetNode.id,
          sourceHandle: "a",
          targetHandle: "b"
        }).then(savedEdgeData => {
          if (savedEdgeData) {
            // Update the edge with the database ID
            const updatedEdge = {
              ...newEdge,
              id: `edge-${savedEdgeData.id}`, // Use the actual database ID
              databaseId: savedEdgeData.id // Store the database ID for future reference
            }
            
            // Update the edge in local state
            setEdges((eds) => {
              // Check if this edge already exists to prevent duplicates
              const existingEdge = eds.find(edge => 
                edge.source === updatedEdge.source && 
                edge.target === updatedEdge.target
              )
              
              if (existingEdge && existingEdge.id !== tempEdgeId) {
                return eds.map(edge => 
                  edge.source === updatedEdge.source && edge.target === updatedEdge.target 
                    ? updatedEdge 
                    : edge
                )
              }
              
              return eds.map(edge => edge.id === tempEdgeId ? updatedEdge : edge)
            })
            
            // Update the edge in workflow context
            if (currentWorkflow) {
              updateEdge(currentWorkflow.id, tempEdgeId, updatedEdge)
            }
          } else {
            // Remove the edge if save failed
            setEdges((eds) => eds.filter(edge => edge.id !== tempEdgeId))
            console.error('Failed to save freeform edge to database, removing from local state')
          }
        }).catch(error => {
          console.error('Error saving freeform edge:', error)
          // Remove the edge if save failed
          setEdges((eds) => eds.filter(edge => edge.id !== tempEdgeId))
        })
      }
    }
    setFreeformSourceNodeId(null)
  }, [freeformSourceNodeId, edges, nodes, currentWorkflow, addEdge, updateEdge, getNodeAtPosition])

  // Add a global mouse move handler to update the placeholder node position
  useEffect(() => {
    const handleMouseMove = (event) => {
      if (placeholderNodeId && reactFlowInstance && isDragging.current) {
        const newPosition = reactFlowInstance.screenToFlowPosition({
          x: event.clientX + 50, // Offset to position ahead of cursor
          y: event.clientY,
        })

        setNodes((nds) =>
          nds.map((node) => {
            if (node.id === placeholderNodeId) {
              return {
                ...node,
                position: newPosition,
              }
            }
            return node
          }),
        )
      }
    }

    // Add the event listener when we're in dragging mode
    if (isDragging.current && placeholderNodeId) {
      window.addEventListener("mousemove", handleMouseMove)
    }

    // Clean up the event listener
    return () => {
      window.removeEventListener("mousemove", handleMouseMove)
    }
  }, [placeholderNodeId, reactFlowInstance, setNodes])

  // Add effect to handle panning prevention
  useEffect(() => {
    // Function to prevent mouse events that would cause panning
    const preventPanning = (e) => {
      if (document.body.classList.contains("disable-flow-panning")) {
        // Only prevent default on middle mouse button (wheel) or when dragging with left button
        if (e.button === 1 || (e.button === 0 && e.type === "mousemove" && e.buttons === 1)) {
          e.stopPropagation()
        }
      }
    }

    // Add event listeners to the document
    document.addEventListener("mousedown", preventPanning, true)
    document.addEventListener("mousemove", preventPanning, true)

    // Clean up event listeners on component unmount
    return () => {
      document.removeEventListener("mousedown", preventPanning, true)
      document.removeEventListener("mousemove", preventPanning, true)
    }
  }, [])

  // Custom node change handler to save positions
  const handleNodesChange = useCallback(
    (changes) => {
      setNodes((nds) => {
        return applyNodeChanges(changes, nds)
      })
    },
    []
  )

  // Function to save node position to database
  const saveNodePositionToDatabase = useCallback(async (nodeId, position) => {
    if (!currentWorkflow) return

    try {
      const csrfToken = csrf_token;

      // Handle temporary node IDs (like taskNode-1234567890) by finding the actual database ID
      let actualNodeId = nodeId;
      let nodeType = null;
      
      if (nodeId.startsWith('step-')) {
        actualNodeId = nodeId.replace('step-', '');
        nodeType = 'step';
      } else if (nodeId.startsWith('trigger-') || nodeId === 'start-trigger') {
        await saveTriggerPositionForNode(currentWorkflow.id, nodeId, position, csrfToken)
        return
      } else {
        // For temporary node IDs (like taskNode-1234567890), try to find the actual database ID
        // by looking up the node in the current workflow context
        const node = nodes.find(n => n.id === nodeId);
        if (node && node.databaseId) {
          // If the node has a databaseId stored at the node level, use that
          actualNodeId = node.databaseId;
          nodeType = node.type === 'requestNode' ? 'step' : 'step';
        } else {
          // For temporary nodes without database IDs, skip saving to database
          // The position will be saved when the node is properly created in the database
          return;
        }
      }

      // Save position based on node type
      if (nodeType === 'step') {
        const response = await fetch(`/workflows/${currentWorkflow.id}/steps/${actualNodeId}/position`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            position: JSON.stringify(position)
          }),
        })

        if (!response.ok) {
          throw new Error(`Failed to update step position: ${response.status}`)
        }
      }
    } catch (error) {
      console.error('Failed to save node position to database:', error)
      // Continue silently - don't interrupt user experience
    }
  }, [currentWorkflow, csrf_token, nodes])

  const handleNodeDragStop = useCallback((_, node) => {
    if (!currentWorkflow || !node?.id || !node?.position) return

    updateNode(currentWorkflow.id, node.id, {
      position: node.position,
    })
    saveNodePositionToDatabase(node.id, node.position)
  }, [currentWorkflow, updateNode, saveNodePositionToDatabase])

  const handleNodeMouseEnter = useCallback((_, node) => {
    hoveredNodeIdRef.current = node.id
  }, [])

  const handleNodeMouseLeave = useCallback(() => {
    hoveredNodeIdRef.current = null
  }, [])

  const handleEdgeClick = useCallback((event, edge) => {
    const isFromTrigger = edge.source === "start-trigger" || edge.source.startsWith("trigger-")
    if (!isFromTrigger) return

    const isMultiSelect = event.ctrlKey || event.metaKey
    const changes = []

    if (isMultiSelect) {
      const currentEdge = edges.find((e) => e.id === edge.id)
      changes.push({
        id: edge.id,
        type: "select",
        selected: !currentEdge?.selected,
      })
    } else {
      edges.forEach((e) => {
        const eIsFromTrigger = e.source === "start-trigger" || e.source.startsWith("trigger-")
        if (!eIsFromTrigger) return
        if (e.id === edge.id) {
          changes.push({
            id: e.id,
            type: "select",
            selected: true,
          })
        } else if (e.selected) {
          changes.push({
            id: e.id,
            type: "select",
            selected: false,
          })
        }
      })
    }

    if (changes.length > 0) {
      const updatedEdges = applyEdgeChanges(changes, edges)
      setEdges(updatedEdges)
    }
  }, [edges, setEdges])

  // Helper function to check if a trigger node has active triggers
  const hasActiveTriggers = (nodeId) => {
    const node = nodes.find(n => n.id === nodeId)
    if (!node || node.type !== 'triggerNode') return false
    
    // Check if the node has enabled triggers in its data
    const enabledTriggers = node.data?.enabledTriggers || []
    return enabledTriggers.length > 0
  }

  // Helper function to check if a trigger node needs configuration
  const needsTriggerConfiguration = (nodeId) => {
    const node = nodes.find(n => n.id === nodeId)
    if (!node || node.type !== 'triggerNode') return false
    
    // Check if the node has enabled triggers in its data
    const enabledTriggers = node.data?.enabledTriggers || []
    return enabledTriggers.length === 0
  }

  // Custom connect handler to save edges
  const handleConnect = useCallback(
    async (connection) => {
      // If no current workflow exists, try to create one
      let targetWorkflow = resolveTargetWorkflow()
      if (!targetWorkflow) {
        // Check if we're on a "new" workflow route
        if (workflowId === "new") {
          try {
            targetWorkflow = await createWorkflow("New Workflow", csrf_token)
          } catch (error) {
            console.error('Failed to create workflow for connection:', error)
            return
          }
        } else {
          // If we're not on a "new" route and no workflow exists, we can't proceed
          return
        }
      }

      // Check if this connection already exists to prevent duplicates
      const existingEdge = edges.find(edge => 
        edge.source === connection.source && 
        edge.target === connection.target &&
        edge.sourceHandle === connection.sourceHandle &&
        edge.targetHandle === connection.targetHandle
      )
      
      if (existingEdge) {
        console.log('Connection already exists, skipping:', connection)
        return
      }

      // Check if trigger nodes have active triggers (for validation display)
      const hasValidTriggers = connection.source === 'start-trigger' || connection.source.startsWith('trigger-') 
        ? hasActiveTriggers(connection.source)
        : true

      // Always allow connections, even if triggers are inactive
      // This allows edges to be created and show validation state
      if (!hasValidTriggers) {
        // Show helpful warning message but still create the connection
        toast({
          title: "No active triggers",
          description: "Connection created but will be inactive until triggers are configured.",
          variant: "destructive",
        })
      }

      // Create a unique temporary ID for the edge
      const tempEdgeId = `temp-edge-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      
      const newEdge = {
        id: tempEdgeId,
        source: connection.source,
        target: connection.target,
        type: 'custom',
        sourceHandle: connection.sourceHandle,
        targetHandle: connection.targetHandle,
        data: { 
          nodes, // Pass nodes data for edge validation
          workflowId: targetWorkflow.id // Pass workflow ID for edge deletion
        }
      }

      try {
        // Add edge to workflow context first
        await addEdge(targetWorkflow.id, newEdge, csrf_token)
      } catch (error) {
        console.error('Failed to add edge to workflow context:', error)
        toast({
          title: "Connection failed",
          description: "Unable to create connection. Please try again.",
          variant: "destructive",
        })
        return
      }
      
      // Update local state
      setEdges((eds) => {
        // Check if this edge already exists to prevent duplicates
        const existingEdge = eds.find(edge => 
          edge.source === newEdge.source && 
          edge.target === newEdge.target &&
          edge.sourceHandle === newEdge.sourceHandle &&
          edge.targetHandle === newEdge.targetHandle
        )
        
        if (existingEdge) {
          console.log('Edge already exists in local state, skipping:', newEdge)
          return eds
        }
        
        return [...eds, newEdge]
      })

      // Save edge to database
      const savedEdgeData = await saveEdgeToDatabase(connection)
      
      // If save failed, remove the edge from local state and workflow context
      if (!savedEdgeData) {
        setEdges((eds) => eds.filter(edge => edge.id !== tempEdgeId))
        // Note: We'd need to add a removeEdge function to the workflow context for this
        console.error('Failed to save edge to database, removing from local state')
      } else {
        // Update the edge with the database ID
        const updatedEdge = {
          ...newEdge,
          id: `edge-${savedEdgeData.id}`, // Use the actual database ID
          databaseId: savedEdgeData.id // Store the database ID for future reference
        }
        
        // Update the edge in local state
        setEdges((eds) => {
          // Check if this edge already exists to prevent duplicates
          const existingEdge = eds.find(edge => 
            edge.source === updatedEdge.source && 
            edge.target === updatedEdge.target &&
            edge.sourceHandle === updatedEdge.sourceHandle &&
            edge.targetHandle === updatedEdge.targetHandle
          )
          
          if (existingEdge && existingEdge.id !== tempEdgeId) {
            return eds.map(edge => 
              edge.source === updatedEdge.source &&
              edge.target === updatedEdge.target &&
              edge.sourceHandle === updatedEdge.sourceHandle &&
              edge.targetHandle === updatedEdge.targetHandle
                ? updatedEdge 
                : edge
            )
          }
          
          return eds.map(edge => edge.id === tempEdgeId ? updatedEdge : edge)
        })
        
        // Update the edge in workflow context
        updateEdge(targetWorkflow.id, tempEdgeId, updatedEdge)
      }
    },
    [resolveTargetWorkflow, createWorkflow, workflowId, addEdge, hasActiveTriggers, edges, updateEdge, nodes, csrf_token, toast]
  )

  // Custom connect end handler for freeform connections
  const handleConnectEnd = useCallback(
    async (event) => {
      if (!sourceNodeId || !reactFlowInstanceRef.current) return

      // Skip freeform connections for ifElseNode - let it use standard ReactFlow connections
      const sourceNode = nodes.find((node) => node.id === sourceNodeId)
      if (sourceNode && sourceNode.type === "ifElseNode") {
        console.log('Skipping freeform connection for ifElseNode - use standard handle connections')
        setSourceNodeId(null)
        setIsInputConnection(false)
        setDropPosition(null)
        menuNodeId.current = null
        isDragging.current = false
        return
      }

      // If no current workflow exists, try to create one
      let targetWorkflow = resolveTargetWorkflow()
      if (!targetWorkflow) {
        // Check if we're on a "new" workflow route
        if (workflowId === "new") {
          try {
            targetWorkflow = await createWorkflow("New Workflow", csrf_token)
          } catch (error) {
            console.error('Failed to create workflow for freeform connection:', error)
            return
          }
        } else {
          // If we're not on a "new" route and no workflow exists, we can't proceed
          return
        }
      }

      // Find the node under the mouse
      const targetNode = getNodeAtPosition(event.clientX, event.clientY)
      
      if (targetNode && targetNode.id !== sourceNodeId) {
        
        // Check if trigger nodes have active triggers (for validation display)
        const hasValidTriggers = sourceNodeId === 'start-trigger' || sourceNodeId.startsWith('trigger-') 
          ? hasActiveTriggers(sourceNodeId)
          : true

        // Always allow connections, even if triggers are inactive
        // This allows edges to be created and show validation state
        if (!hasValidTriggers) {
          // Show helpful warning message but still create the connection
          toast({
            title: "No active triggers",
            description: "Connection created but will be inactive until triggers are configured.",
            variant: "destructive",
          })
        }

        // Determine the correct source handle based on the source node type
        let sourceHandle = "a" // Default
        
        const newEdge = {
          id: `edge-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          source: sourceNodeId,
          target: targetNode.id,
          sourceHandle: sourceHandle,
          targetHandle: "b",
          type: "custom",
          data: { 
            nodes, // Pass nodes data for edge validation
            workflowId: targetWorkflow.id // Pass workflow ID for edge deletion
          }
        }

        // Add edge to workflow context
        await addEdge(targetWorkflow.id, newEdge, csrf_token)
        
        // Update local state
        setEdges((eds) => {
          // Check if this edge already exists to prevent duplicates
          const existingEdge = eds.find(edge => 
            edge.source === newEdge.source && 
            edge.target === newEdge.target
          )
          
          if (existingEdge) {
            return eds
          }
          
          return [...eds, newEdge]
        })

        // Save edge to database
        const savedEdgeData = await saveEdgeToDatabase({
          source: sourceNodeId,
          target: targetNode.id,
          sourceHandle: sourceHandle,
          targetHandle: "b"
        })
        
        // If save failed, remove the edge from local state
        if (!savedEdgeData) {
          setEdges((eds) => eds.filter(edge => edge.id !== newEdge.id))
        } else {
          // Update the edge with the database ID
          const updatedEdge = {
            ...newEdge,
            id: `edge-${savedEdgeData.id}`, // Use the actual database ID
            databaseId: savedEdgeData.id // Store the database ID for future reference
          }
          
          // Update the edge in local state
          setEdges((eds) => {
            // Check if this edge already exists to prevent duplicates
            const existingEdge = eds.find(edge => 
              edge.source === updatedEdge.source && 
              edge.target === updatedEdge.target
            )
            
            if (existingEdge && existingEdge.id !== newEdge.id) {
              return eds.map(edge => 
                edge.source === updatedEdge.source && edge.target === updatedEdge.target 
                  ? updatedEdge 
                  : edge
              )
            }
            
            return eds.map(edge => edge.id === newEdge.id ? updatedEdge : edge)
          })
          
          // Update the edge in workflow context
          updateEdge(targetWorkflow.id, newEdge.id, updatedEdge)
        }
      }

      // Clean up
      setSourceNodeId(null)
      setIsInputConnection(false)
      setDropPosition(null)
      menuNodeId.current = null
      isDragging.current = false
    },
    [sourceNodeId, resolveTargetWorkflow, createWorkflow, workflowId, addEdge, hasActiveTriggers, nodes, edges, csrf_token, updateEdge]
  )

  const onDragOver = useCallback((event) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
  }, [])

  const onConnectStart = useCallback(
    (_, { nodeId, handleType }) => {
      if (handleType === "source" && nodeId) {
        // Find the source node to check its type
        const sourceNode = nodes.find((node) => node.id === nodeId)

        // Set source node ID and input connection flag
        setSourceNodeId(nodeId)
        setIsInputConnection(false)
        isDragging.current = true

        // Skip creating placeholder nodes for TriggerNode and IfElseNode
        if (sourceNode && (sourceNode.type === "triggerNode" || sourceNode.type === "ifElseNode")) {
          // For TriggerNode and IfElseNode, just set dragging state without creating placeholders
          return
        }
      } else if (handleType === "target" && nodeId) {
        // Handle dragging from an input handle
        setSourceNodeId(nodeId)
        setIsInputConnection(true)
        isDragging.current = true

        // Create a placeholder node
        const targetNode = nodes.find((node) => node.id === nodeId)
        // if (targetNode) {
        //   const placeholderId = `placeholder-${Date.now()}`
        //   setPlaceholderNodeId(placeholderId)
        //   menuNodeId.current = placeholderId

        //   // Position the placeholder node to the left of the target node
        //   const placeholderPosition = {
        //     x: targetNode.position.x - 250,
        //     y: targetNode.position.y,
        //   }

        //   // Add the placeholder node
        //   const placeholderNode = {
        //     id: placeholderId,
        //     type: "placeholderNode",
        //     position: placeholderPosition,
        //     data: { label: "Drop to add a block" },
        //     // Placeholder nodes should not be draggable
        //     draggable: false,
        //   }

        //   setNodes((nds) => [...nds, placeholderNode])

        //   // Create a temporary edge
        //   const tempEdgeId = `temp-edge-${Date.now()}`
        //   setTemporaryEdgeId(tempEdgeId)

        //   const tempEdge = {
        //     id: tempEdgeId,
        //     source: placeholderId,
        //     target: nodeId,
        //     sourceHandle: "a",
        //     targetHandle: "b",
        //     type: "custom",
        //   }

        //   setEdges((eds) => [...eds, tempEdge])
        // }
      }
    },
    [nodes, setNodes, setEdges],
  )

  const onDrop = useCallback(
    async (event) => {
      event.preventDefault()

      const type = event.dataTransfer.getData("application/reactflow")

      // Check if the dropped element is valid
      if (typeof type === "undefined" || !type) {
        return
      }

      // If no current workflow exists, try to create one
      let targetWorkflow = resolveTargetWorkflow()
      if (!targetWorkflow) {
        // Check if we're on a "new" workflow route
        if (workflowId === "new") {
          try {
            targetWorkflow = await createWorkflow("New Workflow", csrf_token)
          } catch (error) {
            console.error('Failed to create workflow for node drop:', error)
            return
          }
        } else {
          // If we're not on a "new" route and no workflow exists, we can't proceed
          return
        }
      }
      
      // Set flag to prevent node resets while adding
      setIsAddingNode(true)
      
      try {
      
      // Get node data based on type
      let newNode = {}

      switch (type) {
        case "triggerNode":
          newNode = {
            label: "newTrigger",
            triggerType: "webhook",
            endpoint: "/api/webhook/new",
          }
          break
        case "actionNode":
          newNode = {
            label: "Set Suppression",
            name: "Set Suppression",
            description: "Suppress task creation for matching contracts",
            operation: "set_suppression",
            entityType: "contract",
            entityKeySource: "variable",
            entityKeyVariable: "contractid",
            suppressionType: "workflow_task_creation",
            reasonCode: "wait_full_project_completion",
            reasonText: "",
            scopeType: "workflow",
          }
          break
        case "dataNode":
          newNode = {
            label: "getData",
            dataSource: "database",
            query: "SELECT * FROM table",
          }
          break
        case "aiNode":
          newNode = {
            label: "generateAI",
            model: "gpt-4",
            prompt: "Summarize the following data:",
          }
          break
        case "responseNode":
          newNode = {
            label: "webhookReturn",
            statusCode: 200,
            responseBody: '{ "success": true }',
          }
          break
        case "workflowNode":
          newNode = {
            label: "workflow",
            workflowType: "Retool Workflow",
            selectedWorkflow: "",
            runUntil: "Finished",
            parameterType: "Raw",
            parameters: 'ex: { or { key: "value" }',
          }
          break
        case "branchNode":
          newNode = {
            label: "branch2",
            conditions: [{ type: "if", expression: "value.something === true" }],
          }
          break
        case "ifElseNode":
          newNode = {
            label: "If/Else",
            name: "If/Else",
            description: "Conditional logic based on response data",
            conditions: [
              {
                id: 1,
                type: "if",
                description: "Condition 1",
                operator: "equals",
                value: "",
                field: "status",
                comparisonType: "response_field",
                expression: "",
                responseBodyKey: "",
              }
            ],
            expanded: true
          }
          break
        case "waitNode":
          newNode = {
            label: "Wait",
            name: "Wait",
            description: "Delay/cooldown before the next task can be created",
            durationUnit: "days",
            durationExpression: "1",
            targetTime: "08:00",
            wait_duration_unit: "days",
            wait_duration_expression: "1",
            wait_target_time: "08:00",
            expanded: true,
          }
          break
        case "taskNode":
          // Inherit workflow assignment settings (include shared: true so step assignment is persisted correctly)
          const workflowAssignment = targetWorkflow ? {
            type: targetWorkflow.assignment_type || 'individual',
            user_ids: targetWorkflow.assigned_user_ids || [],
            department_ids: targetWorkflow.assigned_department_ids || [],
            shared: true
          } : { type: 'individual', user_ids: [], department_ids: [], shared: true }
          
          newNode = {
            label: "newTask",
            name: "New Task",
            description: "Task description",
            category: "General",
            priority: "5",
            due: new Date().toISOString().split("T")[0],
            status: "To Do",
            deferrable: 0,
            assignment: workflowAssignment,
          }
          break
        case "requestNode":
          newNode = {
            label: "HTTP Request",
            name: "HTTP Request",
            description: "",
            url: "",
            method: "GET",
            headers: {},
            body: "",
          }
          break
        case "formNode":
          newNode = {
            label: "newForm",
            name: "Dynamic Form",
            description: "Create a dynamic form",
            fields: [
              {
                id: "field-1",
                type: "text",
                label: "Name",
                placeholder: "Enter your name",
                required: true,
                defaultValue: "",
                validation: {
                  minLength: 0,
                  maxLength: 100,
                  pattern: ""
                }
              }
            ],
            settings: {
              submitButtonText: "Submit",
              resetButtonText: "Reset",
              showProgressBar: true,
              allowMultipleSubmissions: false,
              redirectUrl: "",
              successMessage: "Form submitted successfully!",
              errorMessage: "Please check your input and try again."
            }
          }
          break
        case "updateNoteNode":
          newNode = {
            label: "Update Note",
            name: "Update Note",
            description: "Update an LP note",
            operation: "append",
            mode: "job",
            recid: "",
            noteId: "",
            noteText: "",
            categorySource: "label",
            noteCategory: "General",
            nctId: "",
            appendBy: "DME A/R Workflow",
            empId: "",
            noteImportant: false,
            expanded: true,
          }
          break
        case "updateJobNode":
          newNode = {
            label: "Update Job",
            name: "Update Job",
            description: "Update an LP job",
            operation: "update_status",
            jobId: "{{job_id}}",
            jbsId: "",
            jobStatusLabel: "",
            expanded: true,
          }
          break
        case "createNoteNode":
          newNode = {
            label: "Create Note",
            name: "Create Note",
            description: "Create a new LP note",
            mode: "job",
            recid: "{{recid}}",
            noteText: "",
            categorySource: "label",
            noteCategory: "General",
            nctId: "",
            noteImportant: false,
            expanded: true,
          }
          break
        case "sendTextNode":
          newNode = {
            label: "Send SMS",
            name: "Send SMS",
            description: "Send an SMS via Twilio",
            templateId: "",
            recipientSource: "variable",
            recipientPhone: "{{customer_phone}}",
            variableMappings: {},
            messageOverride: "",
            useTemplateMessage: true,
            mediaUrls: [],
            mediaSource: "both",
            expanded: true,
          }
          break
        case "sendEmailNode":
          newNode = {
            label: "Send Email",
            name: "Send Email",
            description: "Send an email via SMTP",
            templateId: "",
            emailAccountId: "",
            recipientSource: "variable",
            recipientEmail: "{{customer_email}}",
            variableMappings: {},
            messageOverride: "",
            subjectOverride: "",
            useTemplateMessage: true,
            useTemplateSubject: true,
            expanded: true,
          }
          break
        default:
          newNode = { label: "newNode" }
      }

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      })

      const id = `${type}-${Date.now()}`

      const newNodeData = {
        ...newNode,
        workflowId: targetWorkflow?.id,
        expanded: true,
      }

      // Nodes that delete via Inertia own their own delete request.
      // Provide a local callback they can fire after a successful Inertia delete
      // so we can strip node + connected edges from React Flow state without
      // touching workflow-context.deleteNode.
      if (type === "updateNoteNode" || type === "updateJobNode" || type === "createNoteNode" || type === "sendTextNode" || type === "sendEmailNode" || type === "taskNode" || type === "ifElseNode") {
        newNodeData.onDeleteNode = (nodeId) => {
          setNodes((nds) => nds.filter((n) => n.id !== nodeId))
          setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
        }
      }

      const newNodeObject = {
        id,
        type,
        position,
        data: newNodeData,
      }

      // For updateNoteNode and createNoteNode we bypass workflow-context.addNode
      // and persist directly through Inertia + a graph refresh so edge creation
      // can use persisted IDs.
      const savedNode = type === "updateNoteNode"
        ? await createUpdateNoteNodeViaInertia(targetWorkflow, newNodeObject)
        : type === "updateJobNode"
          ? await createUpdateJobNodeViaInertia(targetWorkflow, newNodeObject)
        : type === "createNoteNode"
          ? await createCreateNoteNodeViaInertia(targetWorkflow, newNodeObject)
          : type === "sendTextNode"
            ? await createSendTextNodeViaInertia(targetWorkflow, newNodeObject)
          : type === "sendEmailNode"
            ? await createSendEmailNodeViaInertia(targetWorkflow, newNodeObject)
          : await addNode(targetWorkflow.id, newNodeObject, csrf_token)

      const normalizedSavedNode = {
        ...(savedNode || newNodeObject),
        data: {
          ...(newNodeObject?.data || {}),
          ...((savedNode || newNodeObject)?.data || {}),
          workflowId: targetWorkflow?.id,
          databaseId:
            ((savedNode || newNodeObject)?.data?.databaseId) ??
            (savedNode || newNodeObject)?.databaseId ??
            null,
        },
      }

      const persistedNodeId = normalizedSavedNode.id
      
      // Update local state with the saved node (which includes database ID)
      setNodes((nds) => nds.concat(normalizedSavedNode))

      // If we have a source node, create an edge from the source to the new node
      if (sourceNodeId) {
        // Check if this connection already exists to prevent duplicates
        const existingEdge = edges.find(edge => 
          edge.source === sourceNodeId && 
          edge.target === persistedNodeId
        )
        
        if (existingEdge) {
          console.log('Connection already exists, skipping:', { source: sourceNodeId, target: persistedNodeId })
        } else {
          // Create a unique temporary ID for the edge
          const tempEdgeId = `temp-edge-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
          
          let newEdge
          if (isInputConnection) {
            // Create an edge from the new node to the source node
            newEdge = {
              id: tempEdgeId,
              source: persistedNodeId,
              target: sourceNodeId,
              sourceHandle: "a",
              targetHandle: "b",
              type: "custom",
              data: { 
                nodes, // Pass nodes data for edge validation
                workflowId: targetWorkflow.id // Pass workflow ID for edge deletion
              }
            }
          } else {
            // Create an edge from the source node to the new node
            // Determine the correct source handle based on the source node type
            let sourceHandle = "a" // Default
            const sourceNodeData = nodes.find(n => n.id === sourceNodeId)
            
            if (sourceNodeData && sourceNodeData.type === 'ifElseNode') {
              // For IfElseNode, we need to determine which output handle to use
              // Since this is a drop connection, we'll use the first available output handle
              // or the default output handle if no conditions exist
              if (sourceNodeData.data && sourceNodeData.data.conditions && sourceNodeData.data.conditions.length > 0) {
                // Use the first condition's output handle
                sourceHandle = `output-${sourceNodeData.data.conditions[0].id}`
              } else {
                // Use the default output handle
                sourceHandle = "output-default"
              }
            }
            
            newEdge = {
              id: tempEdgeId,
              source: sourceNodeId,
              target: persistedNodeId,
              sourceHandle: sourceHandle,
              targetHandle: "b",
              type: "custom",
              data: { 
                nodes, // Pass nodes data for edge validation
                workflowId: targetWorkflow.id // Pass workflow ID for edge deletion
              }
            }
          }

          // Add edge to workflow context
          await addEdge(targetWorkflow.id, newEdge, csrf_token)
          
          // Update local state
          setEdges((eds) => {
            // Check if this edge already exists to prevent duplicates
            const existingEdge = eds.find(edge => 
              edge.source === newEdge.source && 
              edge.target === newEdge.target
            )
            
            if (existingEdge) {
              return eds
            }
            
            return [...eds, newEdge]
          })

          // Save edge to database
          const savedEdgeData = await saveEdgeToDatabase({
            source: newEdge.source,
            target: newEdge.target,
            sourceHandle: newEdge.sourceHandle,
            targetHandle: newEdge.targetHandle
          })
          
          // If save failed, remove the edge from local state
          if (!savedEdgeData) {
            setEdges((eds) => eds.filter(edge => edge.id !== tempEdgeId))
            console.error('Failed to save edge to database, removing from local state')
          } else {
            // Update the edge with the database ID
            const updatedEdge = {
              ...newEdge,
              id: `edge-${savedEdgeData.id}`, // Use the actual database ID
              databaseId: savedEdgeData.id // Store the database ID for future reference
            }
            
            // Update the edge in local state
            setEdges((eds) => {
              // Check if this edge already exists to prevent duplicates
              const existingEdge = eds.find(edge => 
                edge.source === updatedEdge.source && 
                edge.target === updatedEdge.target
              )
              
              if (existingEdge && existingEdge.id !== tempEdgeId) {
                return eds.map(edge => 
                  edge.source === updatedEdge.source && edge.target === updatedEdge.target 
                    ? updatedEdge 
                    : edge
                )
              }
              
              return eds.map(edge => edge.id === tempEdgeId ? updatedEdge : edge)
            })
            
            // Update the edge in workflow context
            updateEdge(targetWorkflow.id, tempEdgeId, updatedEdge)
          }
        }
      }

      // Clean up the placeholder node and temporary edge
      if (placeholderNodeId) {
        setNodes((nds) => nds.filter((node) => node.id !== placeholderNodeId))
        setPlaceholderNodeId(null)
      }

      if (temporaryEdgeId) {
        setEdges((eds) => eds.filter((edge) => edge.id !== temporaryEdgeId))
        setTemporaryEdgeId(null)
      }

      // Reset state
      setSourceNodeId(null)
      setIsInputConnection(false)
      setDropPosition(null)
      menuNodeId.current = null
      
      } catch (error) {
        console.error('Error dropping node:', error)
        toast({
          title: "Failed to add block",
          description: "Unable to add the block to the workflow. Please try again.",
          variant: "destructive",
        })
      } finally {
        // Clear the adding node flag
        setIsAddingNode(false)
      }
    },
    [screenToFlowPosition, setNodes, resolveTargetWorkflow, createWorkflow, workflowId, addNode, addEdge, sourceNodeId, isInputConnection, placeholderNodeId, temporaryEdgeId, setIsAddingNode, edges, nodes, updateEdge, csrf_token, toast],
  )

  const handleRunWorkflow = async () => {
    const targetWorkflow = resolveTargetWorkflow()
    if (!targetWorkflow?.id) {
      toast({
        title: "Error",
        description: "No workflow selected.",
        variant: "destructive",
      })
      return
    }

    try {
      // Show loading toast
      toast({
        title: "Running workflow...",
        description: `Executing "${workflowName}"...`,
      })

      const csrfToken = csrf_token;

      const response = await fetch(`/workflows/${targetWorkflow.id}/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
          'Accept': 'application/json',
        },
      })

      const result = await response.json()

      if (response.ok) {
        if (result.warning) {
          // Show warning toast
          toast({
            title: "Workflow executed",
            description: result.message,
            variant: "default",
          })
        } else {
          // Show success toast
          toast({
            title: "Success!",
            description: result.message,
            variant: "default",
          })
        }
        
        // Refresh workflow history if the history panel is open
        if (showHistoryPanel) {
          fetchWorkflowHistory()
        }
      } else {
        // Show error toast
        toast({
          title: "Workflow execution failed",
          description: result.error || "An error occurred while running the workflow.",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error('Error running workflow:', error)
      toast({
        title: "Error",
        description: "Failed to run workflow. Please try again.",
        variant: "destructive",
      })
    }
  }

  const handleRunSelectedFlows = async () => {
    const targetWorkflow = resolveTargetWorkflow()
    if (!targetWorkflow?.id) {
      toast({
        title: "Error",
        description: "No workflow selected.",
        variant: "destructive",
      })
      return
    }

    if (selectedEdges.length === 0) {
      toast({
        title: "No paths selected",
        description: "Please select one or more edges from trigger nodes to run.",
        variant: "destructive",
      })
      return
    }

    try {
      // Extract database edge IDs from ReactFlow edge IDs (format: edge-{id})
      const edgeIds = selectedEdges.map(edgeId => {
        // Extract the database ID from the ReactFlow edge ID
        const match = edgeId.match(/^edge-(\d+)$/)
        if (match) {
          return parseInt(match[1], 10)
        }
        // If format is different, try to parse as integer directly
        const parsed = parseInt(edgeId.replace('edge-', ''), 10)
        return isNaN(parsed) ? null : parsed
      }).filter(id => id !== null)

      if (edgeIds.length === 0) {
        toast({
          title: "Invalid edge selection",
          description: "Could not extract valid edge IDs from selected edges.",
          variant: "destructive",
        })
        return
      }

      // Show loading toast
      toast({
        title: "Running selected paths...",
        description: `Executing ${edgeIds.length} selected path${edgeIds.length > 1 ? 's' : ''}...`,
      })

      const csrfToken = csrf_token;

      const response = await fetch(`/workflows/${targetWorkflow.id}/run-selected-paths`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          edge_ids: edgeIds
        }),
      })

      const result = await response.json()

      if (response.ok) {
        // Show success toast
        toast({
          title: "Success!",
          description: result.message || `Successfully executed ${edgeIds.length} path${edgeIds.length > 1 ? 's' : ''}.`,
          variant: "default",
        })
        
        // Clear selected edges
        setSelectedEdges([])
        
        // Deselect edges in ReactFlow
        setEdges(currentEdges => 
          currentEdges.map(edge => ({
            ...edge,
            selected: false
          }))
        )
        
        // Refresh workflow history if the history panel is open
        if (showHistoryPanel) {
          fetchWorkflowHistory()
        }
      } else {
        // Show error toast
        toast({
          title: "Execution failed",
          description: result.error || "An error occurred while running the selected paths.",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error('Error running selected flows:', error)
      toast({
        title: "Error",
        description: "Failed to run selected paths. Please try again.",
        variant: "destructive",
      })
    }
  }

  const handleDeployWorkflow = () => {
    setShowDeployDialog(true)
  }

  const handleConfirmDeploy = () => {
    const targetWorkflow = resolveTargetWorkflow()
    if (!targetWorkflow?.id) return
    
    router.put(`/workflows/${targetWorkflow.id}`, {
      description: workflowDescription,
      status: 'active'
    }, {
      preserveState: true,
      preserveScroll: true,
      onSuccess: () => {
        // Update workflow context with new status and description (skip API call since it's already done)
        updateWorkflow(targetWorkflow.id, {
          description: workflowDescription,
          status: 'active'
        }, true, csrf_token)
        setDeployPopoverOpen(false)
        setHasChanges(false)
        
        // Show success toast
        toast({
          title: "Workflow deployed",
          description: `"${workflowName}" has been successfully deployed and is now active.`,
        })
      },
      onError: (errors) => {
        console.error('Error deploying workflow:', errors)
        toast({
          title: "Deployment failed",
          description: "There was an error deploying your workflow. Please try again.",
          variant: "destructive",
        })
      }
    })
  }

  const handleDeactivateWorkflow = () => {
    const targetWorkflow = resolveTargetWorkflow()
    if (!targetWorkflow?.id) return
    
    router.put(`/workflows/${targetWorkflow.id}`, {
      status: 'inactive'
    }, {
      preserveState: true,
      preserveScroll: true,
      onSuccess: () => {
        // Update workflow context with new status (skip API call since it's already done)
        updateWorkflow(targetWorkflow.id, {
          status: 'inactive'
        }, true, csrf_token)
        setDeployPopoverOpen(false)
        
        // Show success toast
        toast({
          title: "Workflow deactivated",
          description: `"${workflowName}" has been deactivated and is no longer running.`,
        })
      },
      onError: (errors) => {
        console.error('Error deactivating workflow:', errors)
        toast({
          title: "Deactivation failed",
          description: "There was an error deactivating your workflow. Please try again.",
          variant: "destructive",
        })
      }
    })
  }

  const handleUpdateDescription = () => {
    if (!currentWorkflow || workflowDescription === currentWorkflow.description) return
    
    router.put(`/workflows/${currentWorkflow.id}`, {
      description: workflowDescription
    }, {
      preserveState: true,
      preserveScroll: true,
      onSuccess: () => {
        // Update workflow context with new description (skip API call since it's already done)
        updateWorkflow(currentWorkflow.id, {
          description: workflowDescription
        }, true, csrf_token)
        setDeployPopoverOpen(false)
        
        // Show success toast
        toast({
          title: "Workflow updated",
          description: `"${workflowName}" description has been updated successfully.`,
        })
      },
      onError: (errors) => {
        console.error('Error updating workflow description:', errors)
        toast({
          title: "Update failed",
          description: "There was an error updating your workflow. Please try again.",
          variant: "destructive",
        })
      }
    })
  }

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "Never"
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  // Helper to format date as 'YYYY-MM-DD HH:mm:ss' for backend compatibility
  const formatDateTime = (dateString) => {
    if (!dateString) return null;
    if (dateString.startsWith('~')) return dateString;
    if (dateString.includes('{{')) return dateString;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    // Always set time to 23:59:59 for due dates (to match backend)
    // Use UTC methods to avoid timezone issues
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth()+1).padStart(2,'0')}-${String(date.getUTCDate()).padStart(2,'0')} 23:59:59`;
  }

  // Get status badge variant
  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case "active":
        return "default"
      case "inactive":
        return "secondary"
      case "draft":
        return "outline"
      default:
        return "outline"
    }
  }

  // Get button text and variant based on workflow status
  const getButtonConfig = () => {
    switch (currentWorkflow?.status) {
      case "active":
        return { text: "Manage", variant: "default" }
      case "inactive":
        return { text: "Deploy", variant: "default" }
      case "draft":
        return { text: "Deploy", variant: "default" }
      default:
        return { text: "Deploy", variant: "default" }
    }
  }

  const handleOpenTriggersPanel = (panel, workflowId, nodeId, enabledTriggers) => {
    setOpenPanel(panel || "triggers")
    setShowTriggersPanel(true)
    setShowBlocksPanel(false)
    setShowOutlinePanel(false)
    
    // Store the workflowId and nodeId for the triggers panel
    setTriggersContext({ workflowId, nodeId, enabledTriggers })
  }

  const handleOpenBlocksPanel = () => {
    setOpenPanel("blocks")
    setShowBlocksPanel(true)
    setShowTriggersPanel(false)
    setShowOutlinePanel(false)
  }

  const handleOpenOutlinePanel = () => {
    setOpenPanel("outline")
    setShowOutlinePanel(true)
    setShowBlocksPanel(false)
    setShowTriggersPanel(false)
  }

  const handleOpenHistoryPanel = () => {
    setOpenPanel("history")
    setShowHistoryPanel(true)
    setShowBlocksPanel(false)
    setShowTriggersPanel(false)
    setShowOutlinePanel(false)
    
    // Fetch workflow history when opening the panel
    if (currentWorkflow && workflowHistory.length === 0) {
      fetchWorkflowHistory()
    }
  }

  const fetchWorkflowHistory = async (filters = {}) => {
    if (!currentWorkflow) return
    
    setHistoryLoading(true)
    setHistoryError(null)
    
    try {
      // Build query parameters
      const params = new URLSearchParams()
      if (filters.status && filters.status !== 'all') {
        params.append('status', filters.status)
      }
      if (filters.search) {
        params.append('search', filters.search)
      }
      if (filters.dateRange && (filters.dateRange.from || filters.dateRange.to)) {
        params.append('dateRange', JSON.stringify(filters.dateRange))
      }
      if (filters.per_page) {
        params.append('per_page', filters.per_page)
      }
      if (filters.page) {
        params.append('page', filters.page)
      }
      
      const queryString = params.toString()
      const url = `/workflows/${currentWorkflow.id}/history${queryString ? `?${queryString}` : ''}`
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrf_token,
          'Accept': 'application/json',
        },
      })
      
      if (!response.ok) {
        throw new Error(`Failed to fetch workflow history: ${response.status}`)
      }
      
      const data = await response.json()
      setWorkflowHistory(data.data || [])
      setHistoryPagination(data.pagination || null)
    } catch (error) {
      console.error('Error fetching workflow history:', error)
      setHistoryError(error.message)
      toast({
        title: "Failed to load history",
        description: "Could not load workflow execution history.",
        variant: "destructive",
      })
    } finally {
      setHistoryLoading(false)
    }
  }

  const handleOpenConnectionMenu = (position, nodeId, isInput = false) => {
    // Just use the screen coordinates directly for the menu position
    setConnectionMenuPosition(position)
    setShowConnectionMenu(true)
    setSourceNodeId(nodeId)
    setIsInputConnection(isInput)

    // Add a click handler to the document to close the menu when clicking outside
    document.addEventListener("click", handleCloseConnectionMenu)
  }

  const handleCloseConnectionMenu = (event) => {
    // If the event is from a menu item click, don't close the menu
    if (event && event.target && event.target.closest(".connection-blocks-menu")) {
      return
    }

    setShowConnectionMenu(false)
    document.removeEventListener("click", handleCloseConnectionMenu)

    // Clean up the placeholder node and temporary edge if the menu is closed without selecting a block
    if (placeholderNodeId) {
      setNodes((nds) => nds.filter((node) => node.id !== placeholderNodeId))
      setPlaceholderNodeId(null)
    }

    if (temporaryEdgeId) {
      setEdges((eds) => eds.filter((edge) => edge.id !== temporaryEdgeId))
      setTemporaryEdgeId(null)
    }
  }

  const addBlockToWorkflow = async (blockId) => {
    // If no current workflow exists, try to create one
    let targetWorkflow = resolveTargetWorkflow()
    if (!targetWorkflow) {
      // Check if we're on a "new" workflow route
      if (workflowId === "new") {
        try {
          targetWorkflow = await createWorkflow("New Workflow", csrf_token)
        } catch (error) {
          console.error('Failed to create workflow for block addition:', error)
          return
        }
      } else {
        // If we're not on a "new" route and no workflow exists, we can't proceed
        return
      }
    }

    // Set flag to prevent node resets while adding
    setIsAddingNode(true)
    
    try {
    // Map block IDs to node types and their default data
    const blockToNodeType = {
      // "resource-query": "dataNode",
      task: "taskNode",
      action: "actionNode",
      // "ai-action": "aiNode",
      response: "responseNode",
      request: "requestNode",
      form: "formNode",
      ifElse: "ifElseNode",
      wait: "waitNode",
      updateNote: "updateNoteNode",
      updateJob: "updateJobNode",
      createNote: "createNoteNode",
      sendText: "sendTextNode",
      sendEmail: "sendEmailNode",
      // workflow: "workflowNode",
      // filter: "actionNode",
      // branch: "branchNode",
      // loop: "actionNode",
      // wait: "actionNode",
      // "loop-legacy": "actionNode",
    }

    const nodeType = blockToNodeType[blockId] || "taskNode";
    
    // Get node data based on type
    let nodeData = {}
    switch (nodeType) {
      case "triggerNode":
        nodeData = {
          label: "newTrigger",
          triggerType: "webhook",
          endpoint: "/api/webhook/new",
        }
        break
      case "actionNode":
        nodeData = {
          label: "Set Suppression",
          name: "Set Suppression",
          description: "Suppress task creation for matching contracts",
          operation: "set_suppression",
          entityType: "contract",
          entityKeySource: "variable",
          entityKeyVariable: "contractid",
          suppressionType: "workflow_task_creation",
          reasonCode: "wait_full_project_completion",
          reasonText: "",
          scopeType: "workflow",
        }
        break
      case "updateNoteNode":
        nodeData = {
          label: "Update Note",
          name: "Update Note",
          description: "Update an LP note via lp/saveNote.php",
          operation: "append",
          mode: "job",
          recid: "",
          noteId: "",
          noteText: "",
          categorySource: "label",
          noteCategory: "General",
          nctId: "",
          appendBy: "DME A/R Workflow",
          empId: "",
          noteImportant: false,
          expanded: true,
        }
        break
      case "createNoteNode":
        nodeData = {
          label: "Create Note",
          name: "Create Note",
          description: "Create a new LP note via lp/saveNote.php",
          mode: "job",
          recid: "{{recid}}",
          noteText: "",
          categorySource: "label",
          noteCategory: "General",
          nctId: "",
          noteImportant: false,
          expanded: true,
        }
        break
      case "updateJobNode":
        nodeData = {
          label: "Update Job",
          name: "Update Job",
          description: "Update an LP job via lp/updateJob.php",
          operation: "update_status",
          jobId: "{{job_id}}",
          jbsId: "",
          jobStatusLabel: "",
          expanded: true,
        }
        break
      case "sendTextNode":
        nodeData = {
          label: "Send SMS",
          name: "Send SMS",
          description: "Send an SMS via Twilio",
          templateId: "",
          recipientSource: "variable",
          recipientPhone: "{{customer_phone}}",
          variableMappings: {},
          messageOverride: "",
          useTemplateMessage: true,
          mediaUrls: [],
          mediaSource: "both",
          expanded: true,
        }
        break
      case "sendEmailNode":
        nodeData = {
          label: "Send Email",
          name: "Send Email",
          description: "Send an email via SMTP",
          templateId: "",
          emailAccountId: "",
          recipientSource: "variable",
          recipientEmail: "{{customer_email}}",
          variableMappings: {},
          messageOverride: "",
          subjectOverride: "",
          useTemplateMessage: true,
          useTemplateSubject: true,
          expanded: true,
        }
        break
      case "dataNode":
        nodeData = {
          label: "getData",
          dataSource: "database",
          query: "SELECT * FROM table",
        }
        break
      case "aiNode":
        nodeData = {
          label: "generateAI",
          model: "gpt-4",
          prompt: "Summarize the following data:",
        }
        break
      case "responseNode":
        nodeData = {
          label: "webhookReturn",
          statusCode: 200,
          responseBody: '{ "success": true }',
        }
        break
      case "workflowNode":
        nodeData = {
          label: "workflow",
          workflowType: "Retool Workflow",
          selectedWorkflow: "",
          runUntil: "Finished",
          parameterType: "Raw",
          parameters: 'ex: { or { key: "value" }',
        }
        break
      case "branchNode":
        nodeData = {
          label: "branch2",
          conditions: [{ type: "if", expression: "value.something === true" }],
        }
        break
      case "ifElseNode":
        nodeData = {
          label: "If/Else",
          name: "If/Else",
          description: "Conditional logic based on response data",
          conditions: [
            {
              id: 1,
              type: "if",
              description: "Condition 1",
              operator: "equals",
              value: "",
              field: "status",
              comparisonType: "response_field",
              expression: "",
              responseBodyKey: "",
            }
          ],
          expanded: true
        }
        break
      case "waitNode":
        nodeData = {
          label: "Wait",
          name: "Wait",
          description: "Delay/cooldown before the next task can be created",
          durationUnit: "days",
          durationExpression: "1",
          targetTime: "08:00",
          wait_duration_unit: "days",
          wait_duration_expression: "1",
          wait_target_time: "08:00",
          expanded: true,
        }
        break
      case "taskNode":
        // Inherit workflow assignment settings (include shared: true so step assignment is persisted correctly)
        const workflowAssignment = targetWorkflow ? {
          type: targetWorkflow.assignment_type || 'individual',
          user_ids: targetWorkflow.assigned_user_ids || [],
          department_ids: targetWorkflow.assigned_department_ids || [],
          shared: true
        } : { type: 'individual', user_ids: [], department_ids: [], shared: true }
        
        nodeData = {
          label: "newTask",
          name: "New Task",
          description: "Task description",
          category: "General",
          priority: "5",
          due: new Date().toISOString().split("T")[0],
          status: "To Do",
          deferrable: 0,
          assignment: workflowAssignment,
        }
        break
      case "formNode":
        nodeData = {
          label: "newForm",
          name: "Dynamic Form",
          description: "Create a dynamic form",
          fields: [
            {
              id: "field-1",
              type: "text",
              label: "Name",
              placeholder: "Enter your name",
              required: true,
              defaultValue: "",
              validation: {
                minLength: 0,
                maxLength: 100,
                pattern: ""
              }
            }
          ],
          settings: {
            submitButtonText: "Submit",
            resetButtonText: "Reset",
            showProgressBar: true,
            allowMultipleSubmissions: false,
            redirectUrl: "",
            successMessage: "Form submitted successfully!",
            errorMessage: "Please check your input and try again."
          }
        }
        break
      default:
        nodeData = { label: "newNode" }
    }

    // Calculate position for the new node
    let nodePosition
    if (dropPosition) {
      // Use the stored drop position if available
      nodePosition = dropPosition
    } else if (sourceNodeId) {
      // Find the source node
      const sourceNode = nodes.find((node) => node.id === sourceNodeId)
      if (sourceNode) {
        if (isInputConnection) {
          // Position the new node to the left of the source node
          nodePosition = {
            x: sourceNode.position.x - 250,
            y: sourceNode.position.y,
          }
        } else {
          // Position the new node to the right of the source node
          nodePosition = {
            x: sourceNode.position.x + 250,
            y: sourceNode.position.y,
          }
        }
      } else {
        nodePosition = { x: 300, y: 100 }
      }
    } else {
      // If no specific position is set, use the center of the viewport
      nodePosition = screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      })
    }

    const newNode = {
      id: `${nodeType}-${Date.now()}`,
      type: nodeType,
      position: nodePosition,
      data: {
        ...nodeData,
        workflowId: targetWorkflow?.id,
        expanded: true,
      }
    }

    // updateNoteNode and createNoteNode own their own delete via Inertia.
    // Inject the local delete callback before persistence so node components
    // can self-clean React Flow state after successful delete.
    if (nodeType === "updateNoteNode" || nodeType === "updateJobNode" || nodeType === "createNoteNode" || nodeType === "sendTextNode" || nodeType === "sendEmailNode") {
      newNode.data.onDeleteNode = (nodeId) => {
        setNodes((nds) => nds.filter((n) => n.id !== nodeId))
        setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
      }
    }

    // updateNoteNode and createNoteNode bypass workflow-context.addNode and
    // persist directly through Inertia + a graph refresh.
    const savedNode = nodeType === "updateNoteNode"
      ? await createUpdateNoteNodeViaInertia(targetWorkflow, newNode)
      : nodeType === "updateJobNode"
        ? await createUpdateJobNodeViaInertia(targetWorkflow, newNode)
      : nodeType === "createNoteNode"
        ? await createCreateNoteNodeViaInertia(targetWorkflow, newNode)
        : nodeType === "sendTextNode"
          ? await createSendTextNodeViaInertia(targetWorkflow, newNode)
        : nodeType === "sendEmailNode"
          ? await createSendEmailNodeViaInertia(targetWorkflow, newNode)
        : await addNode(targetWorkflow.id, newNode, csrf_token)
    
    const normalizedSavedNode = {
      ...(savedNode || newNode),
      data: {
        ...(newNode?.data || {}),
        ...((savedNode || newNode)?.data || {}),
        workflowId: targetWorkflow?.id,
        databaseId:
          ((savedNode || newNode)?.data?.databaseId) ??
          (savedNode || newNode)?.databaseId ??
          null,
      },
    }

    // Update local state with the saved node (which includes database ID)
    setNodes((nds) => [...nds, normalizedSavedNode])

    // If we have a source node, create an edge from the source to the new node
    if (sourceNodeId) {
      let newEdge
      if (isInputConnection) {
        // Create an edge from the new node to the source node
        newEdge = {
          id: `e-${newNode.id}-${sourceNodeId}`,
          source: newNode.id,
          target: sourceNodeId,
          sourceHandle: "a",
          targetHandle: "b",
          type: "custom",
          data: { 
            nodes, // Pass nodes data for edge validation
            workflowId: targetWorkflow.id // Pass workflow ID for edge deletion
          }
        }
      } else {
        // Create an edge from the source node to the new node
        // Determine the correct source handle based on the source node type
        let sourceHandle = "a" // Default
        const sourceNodeData = nodes.find(n => n.id === sourceNodeId)
        
        if (sourceNodeData && sourceNodeData.type === 'ifElseNode') {
          // For IfElseNode, we need to determine which output handle to use
          // Since this is a block addition, we'll use the first available output handle
          // or the default output handle if no conditions exist
          if (sourceNodeData.data && sourceNodeData.data.conditions && sourceNodeData.data.conditions.length > 0) {
            // Use the first condition's output handle
            sourceHandle = `output-${sourceNodeData.data.conditions[0].id}`
          } else {
            // Use the default output handle
            sourceHandle = "output-default"
          }
        }
        
        newEdge = {
          id: `e-${sourceNodeId}-${newNode.id}`,
          source: sourceNodeId,
          target: newNode.id,
          sourceHandle: sourceHandle,
          targetHandle: "b",
          type: "custom",
          data: { 
            nodes, // Pass nodes data for edge validation
            workflowId: targetWorkflow.id // Pass workflow ID for edge deletion
          }
        }
      }

      // Add edge to workflow context
      await addEdge(targetWorkflow.id, newEdge, csrf_token)
      
      // Update local state
      setEdges((eds) => {
        // Check if this edge already exists to prevent duplicates
        const existingEdge = eds.find(edge => 
          edge.source === newEdge.source && 
          edge.target === newEdge.target
        )
        
        if (existingEdge) {
          return eds
        }
        
        return [...eds, newEdge]
      })

      // Save edge to database
      const savedEdgeData = await saveEdgeToDatabase({
        source: newEdge.source,
        target: newEdge.target,
        sourceHandle: newEdge.sourceHandle,
        targetHandle: newEdge.targetHandle
      })
      
      // If save failed, remove the edge from local state
      if (!savedEdgeData) {
        setEdges((eds) => eds.filter(edge => edge.id !== newEdge.id))
      } else {
        // Update the edge with the database ID
        const updatedEdge = {
          ...newEdge,
          id: `edge-${savedEdgeData.id}`, // Use the actual database ID
          databaseId: savedEdgeData.id // Store the database ID for future reference
        }
        
        // Update the edge in local state
        setEdges((eds) => {
          // Check if this edge already exists to prevent duplicates
          const existingEdge = eds.find(edge => 
            edge.source === updatedEdge.source && 
            edge.target === updatedEdge.target
          )
          
          if (existingEdge && existingEdge.id !== newEdge.id) {
            return eds.map(edge => 
              edge.source === updatedEdge.source && edge.target === updatedEdge.target 
                ? updatedEdge 
                : edge
            )
          }
          
          return eds.map(edge => edge.id === newEdge.id ? updatedEdge : edge)
        })
        
        // Update the edge in workflow context
        updateEdge(targetWorkflow.id, newEdge.id, updatedEdge)
      }
    }

    // Clean up the placeholder node and temporary edge
    if (placeholderNodeId) {
      setNodes((nds) => nds.filter((node) => node.id !== placeholderNodeId))
      setPlaceholderNodeId(null)
    }

    if (temporaryEdgeId) {
      setEdges((eds) => eds.filter((edge) => edge.id !== temporaryEdgeId))
      setTemporaryEdgeId(null)
    }

    // Reset state
    setSourceNodeId(null)
    setIsInputConnection(false)
    setDropPosition(null)
    menuNodeId.current = null
    
    } catch (error) {
      console.error('Error adding block to workflow:', error)
      toast({
        title: "Failed to add block",
        description: "Unable to add the block to the workflow. Please try again.",
        variant: "destructive",
      })
    } finally {
      // Clear the adding node flag
      setIsAddingNode(false)
    }
  }

  // Set up the flow after it's initialized
  const onInit = (instance) => {
    reactFlowInstanceRef.current = instance
    setReactFlowInstance(instance)
  }

  // Add non-passive wheel event listener to handle Ctrl+scroll zooming
  useEffect(() => {
    const handleWheel = (event) => {
      // Check if the event target is within an AssignmentSelector dropdown
      const assignmentDropdown = event.target.closest('.assignment-selector-dropdown')
      if (assignmentDropdown) {
        console.log('AssignmentSelector dropdown detected, allowing scroll')
        // Don't stop propagation - let the dropdown handle it
        return
      }

      // Check if the event target is within a DynamicDateSelector dropdown
      const dynamicDateDropdown = event.target.closest('.dynamic-date-select-menu')
      if (dynamicDateDropdown) {
        console.log('DynamicDateSelector dropdown detected, allowing scroll')
        // Don't stop propagation - let the dropdown handle it
        return
      }

      // Only zoom if Ctrl key is pressed
      if (event.ctrlKey && reactFlowInstance) {
        event.preventDefault()
        const currentZoom = reactFlowInstance.getZoom()
        const delta = event.deltaY > 0 ? -0.1 : 0.1
        const newZoom = currentZoom + delta
        // Allow unlimited zooming - no constraints
        reactFlowInstance.zoomTo(newZoom)
      }
    }

    // Add the event listener with non-passive option
    const reactFlowElement = document.querySelector('.react-flow')
    if (reactFlowElement) {
      reactFlowElement.addEventListener('wheel', handleWheel, { passive: false })
    }

    // Cleanup
    return () => {
      if (reactFlowElement) {
        reactFlowElement.removeEventListener('wheel', handleWheel)
      }
    }
  }, [reactFlowInstance])

  const toggleMiniMap = () => {
    // Toggle the minimap
    setShowMiniMap(!showMiniMap)

    // Hide the button when showing the minimap
    if (!showMiniMap) {
      setShowMapButton(false)
    }

    // If we're hiding the minimap, show the button again after a short delay
    if (showMiniMap) {
      setTimeout(() => {
        setShowMapButton(true)
      }, 300)
    }
  }

  // Double click on the workflow area to show the button again
  const handleWorkflowClick = () => {
    if (!showMapButton && !showMiniMap) {
      setShowMapButton(true)
    }
    
    // Hide edge delete buttons when clicking on the workflow area
    const event = new CustomEvent('hideEdgeDeleteButtons')
    window.dispatchEvent(event)
  }



  // Function to delete an edge from the database
  const deleteEdgeFromDatabase = async (edgeId) => {
    if (!currentWorkflow) {
      console.warn('No current workflow available for deleting edge')
      return false
    }

    try {
      const csrfToken = csrf_token;

      // Find the edge to get its database ID
      const edge = edges.find(e => e.id === edgeId)
      if (!edge) {
        console.warn('Edge not found:', edgeId)
        return false
      }

      // Parse the edge ID to get the database ID (format: edge-{id})
      const edgeDatabaseId = edgeId.replace('edge-', '')
      
      const response = await fetch(`/workflows/${currentWorkflow.id}/edges/${edgeDatabaseId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-TOKEN': csrfToken,
          'Accept': 'application/json',
        },
      })

      if (!response.ok) {
        console.warn(`Failed to delete edge from database: ${response.status}`)
        return false
      }

      return true
    } catch (error) {
      console.error('Failed to delete edge from database:', error)
      return false
    }
  }



  // Function to handle node data changes
  const handleNodeDataChange = async (nodeId, newData) => {
    const resolvedWorkflowId = currentWorkflow?.id || workflow?.id || workflowId

    const mergeUpdateJobNodeData = (existingData, incomingData) => {
      const merged = { ...existingData, ...incomingData }
      const hasUpdateJobFields =
        incomingData.jbsId !== undefined
        || incomingData.jobId !== undefined
        || incomingData.operation !== undefined
        || incomingData.jobStatusLabel !== undefined
        || incomingData.settings?.jbs_id !== undefined

      if (!hasUpdateJobFields) {
        return merged
      }

      const nextSettings = {
        ...(existingData?.settings || {}),
        ...(incomingData.settings || {}),
        operation: incomingData.operation ?? incomingData.settings?.operation ?? existingData?.settings?.operation ?? 'update_status',
        job_id: incomingData.jobId ?? incomingData.settings?.job_id ?? existingData?.settings?.job_id ?? '',
        jbs_id: incomingData.jbsId ?? incomingData.settings?.jbs_id ?? existingData?.settings?.jbs_id ?? '',
        job_status_label: incomingData.jobStatusLabel ?? incomingData.settings?.job_status_label ?? existingData?.settings?.job_status_label ?? '',
      }

      return {
        ...merged,
        operation: nextSettings.operation,
        jobId: nextSettings.job_id,
        jbsId: nextSettings.jbs_id,
        jobStatusLabel: nextSettings.job_status_label,
        settings: nextSettings,
      }
    }

    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId
          ? { ...node, data: mergeUpdateJobNodeData(node.data, newData) }
          : node,
      ),
    )

    if (!resolvedWorkflowId || resolvedWorkflowId === 'new') {
      return
    }

    // Handle temporary node IDs (like taskNode-1234567890) by finding the actual database ID
    let actualNodeId = nodeId;
    let nodeType = null;
    
    if (nodeId.startsWith('step-')) {
      actualNodeId = nodeId.replace('step-', '');
      nodeType = 'step';
    } else if (nodeId.startsWith('trigger-')) {
      actualNodeId = nodeId.replace('trigger-', '');
      nodeType = 'trigger';
    } else {
      // For temporary node IDs (like taskNode-1234567890), try to find the actual database ID
      const node = nodes.find(n => n.id === nodeId);
      if (node && node.databaseId) {
        // If the node has a databaseId stored at the node level, use that
        actualNodeId = node.databaseId;
        nodeType = node.type === 'requestNode' ? 'step' : 'step';
      } else {
        // For temporary nodes without database IDs, skip saving to database
        // The data will be saved when the node is properly created in the database
        return;
      }
    }

    // Save node data to database based on node type
    if (nodeType === 'step') {
      const saveGeneration = (nodeStepSaveGenerationRef.current.get(nodeId) ?? 0) + 1
      nodeStepSaveGenerationRef.current.set(nodeId, saveGeneration)

      const previousSave = nodeStepSaveQueueRef.current.get(nodeId) ?? Promise.resolve()
      const saveTask = previousSave
        .catch(() => {})
        .then(async () => {
          try {
            const csrfToken = csrf_token;

            // Determine step type based on the node type or data
            const node = nodes.find(n => n.id === nodeId);
            const isRequestNode = node?.type === 'requestNode' || newData.url !== undefined;
            const isFormNode = node?.type === 'formNode' || newData.form_id !== undefined;
            const isIfElseNode = node?.type === 'ifElseNode' || newData.conditions !== undefined;
            const isCreateNoteNode = node?.type === 'createNoteNode';
            const isSendTextNode = node?.type === 'sendTextNode';
            const isSendEmailNode = node?.type === 'sendEmailNode';
            const isUpdateJobNode = node?.type === 'updateJobNode'
              || (newData.jobId !== undefined && newData.jbsId !== undefined && node?.type !== 'updateNoteNode');
            const isUpdateNoteNode = !isCreateNoteNode && !isSendTextNode && !isSendEmailNode && !isUpdateJobNode && (
              node?.type === 'updateNoteNode'
              || newData.mode !== undefined
              || newData.recid !== undefined
              || newData.noteId !== undefined
              || newData.noteText !== undefined
            );
            const isActionNode = !isUpdateNoteNode && !isUpdateJobNode && (
              node?.type === 'actionNode'
              || newData.entityType !== undefined
              || (newData.settings && newData.settings.operation !== undefined && newData.settings.entity_type !== undefined)
              || (node?.type !== 'updateNoteNode' && node?.type !== 'updateJobNode' && newData.operation !== undefined && newData.entityKeyVariable !== undefined)
            );
            const isWaitNode = node?.type === 'waitNode'
              || newData.durationUnit !== undefined
              || newData.durationExpression !== undefined
              || newData.targetTime !== undefined
              || newData.wait_duration_unit !== undefined
              || newData.wait_duration_expression !== undefined
              || newData.wait_target_time !== undefined;
            const formattedDue = formatDateTime(newData.due)

            const stepData = {
              step_type: isRequestNode
                ? 'request'
                : isFormNode
                  ? 'form'
                  : isIfElseNode
                    ? 'if_else'
                    : isWaitNode
                      ? 'wait'
                      : isSendTextNode
                        ? 'send_text'
                      : isSendEmailNode
                        ? 'send_email'
                      : isCreateNoteNode
                        ? 'create_note'
                      : isUpdateJobNode
                        ? 'update_job'
                      : isUpdateNoteNode
                        ? 'update_note'
                        : isActionNode
                          ? 'action'
                          : 'task',
              workflow_id: resolvedWorkflowId,
              name: newData.name,
              description: newData.description,
              category: newData.category,
              priority: newData.priority,
              due: formattedDue,
              status: newData.status,
              deferrable: newData.deferrable,
              missable: newData.missable,
              task_type: newData.taskTypeCategories || [],
              assignee_id: newData.assignee_id,
              link: newData.link || '',
              link_title: newData.link_title || '',
              checklist: newData.checklist || [],
              assignment: newData.assignment || null,
            }

            if (!isRequestNode && newData.settings) {
              stepData.settings = newData.settings
            }

            if (isRequestNode) {
              stepData.url = newData.url;
              stepData.method = newData.method;
              stepData.headers = newData.headers;
              stepData.body = newData.body;
              stepData.response = newData.response;
              stepData.extracted_variables = newData.extractedVariables;
            }

            if (isFormNode) {
              stepData.fields = newData.fields;
              stepData.settings = newData.settings;
            }

            if (isIfElseNode) {
              stepData.conditions = newData.conditions;
            }

            if (isWaitNode) {
              stepData.wait_duration_unit = newData.durationUnit ?? newData.wait_duration_unit ?? 'days';
              stepData.wait_duration_expression = newData.durationExpression ?? newData.wait_duration_expression ?? '1';
              stepData.wait_target_time = newData.targetTime ?? newData.wait_target_time ?? '08:00';
            }

            if (isActionNode) {
              stepData.settings = {
                operation: newData.operation ?? newData.settings?.operation ?? 'set_suppression',
                entity_type: newData.entityType ?? newData.settings?.entity_type ?? 'contract',
                entity_key_source: newData.entityKeySource ?? newData.settings?.entity_key_source ?? 'variable',
                entity_key_variable: newData.entityKeyVariable ?? newData.settings?.entity_key_variable ?? 'contractid',
                suppression_type: newData.suppressionType ?? newData.settings?.suppression_type ?? 'workflow_task_creation',
                reason_code: newData.reasonCode ?? newData.settings?.reason_code ?? 'wait_full_project_completion',
                reason_text: newData.reasonText ?? newData.settings?.reason_text ?? '',
                scope_type: newData.scopeType ?? newData.settings?.scope_type ?? 'workflow',
              }
            }

            if (isUpdateJobNode) {
              stepData.settings = {
                operation: newData.operation ?? newData.settings?.operation ?? 'update_status',
                job_id: newData.jobId ?? newData.settings?.job_id ?? '',
                jbs_id: newData.jbsId ?? newData.settings?.jbs_id ?? '',
                job_status_label: newData.jobStatusLabel ?? newData.settings?.job_status_label ?? '',
              }
            }

            if (isUpdateNoteNode) {
              stepData.settings = {
                operation: newData.operation ?? newData.settings?.operation ?? 'append',
                mode: newData.mode ?? newData.settings?.mode ?? 'job',
                recid: newData.recid ?? newData.settings?.recid ?? '',
                note_id: newData.noteId ?? newData.settings?.note_id ?? '',
                note_notes: newData.noteText ?? newData.settings?.note_notes ?? '',
                category_source: newData.categorySource ?? newData.settings?.category_source ?? 'label',
                note_category: newData.noteCategory ?? newData.settings?.note_category ?? 'General',
                nct_id: newData.nctId ?? newData.settings?.nct_id ?? '',
                append_by: newData.appendBy ?? newData.settings?.append_by ?? '',
                emp_id: newData.empId ?? newData.settings?.emp_id ?? '',
                note_important: typeof newData.noteImportant === 'boolean'
                  ? newData.noteImportant
                  : Boolean(newData.settings?.note_important),
              }
            }

            if (isCreateNoteNode) {
              stepData.settings = {
                mode: newData.mode ?? newData.settings?.mode ?? 'job',
                recid: newData.recid ?? newData.settings?.recid ?? '',
                note_notes: newData.noteText ?? newData.settings?.note_notes ?? '',
                category_source: newData.categorySource ?? newData.settings?.category_source ?? 'label',
                note_category: newData.noteCategory ?? newData.settings?.note_category ?? 'General',
                nct_id: newData.nctId ?? newData.settings?.nct_id ?? '',
                note_important: typeof newData.noteImportant === 'boolean'
                  ? newData.noteImportant
                  : Boolean(newData.settings?.note_important),
              }
            }

            if (isSendTextNode) {
              stepData.settings = {
                template_id: newData.templateId ?? newData.settings?.template_id ?? '',
                recipient_source: newData.recipientSource ?? newData.settings?.recipient_source ?? 'variable',
                recipient_phone: newData.recipientPhone ?? newData.settings?.recipient_phone ?? '',
                variable_mappings: newData.variableMappings ?? newData.settings?.variable_mappings ?? {},
                message_override: newData.messageOverride ?? newData.settings?.message_override ?? '',
                use_template_message: typeof newData.useTemplateMessage === 'boolean'
                  ? newData.useTemplateMessage
                  : Boolean(newData.settings?.use_template_message ?? true),
                media_urls: newData.mediaUrls ?? newData.settings?.media_urls ?? [],
                media_source: newData.mediaSource ?? newData.settings?.media_source ?? 'both',
              }
            }

            if (isSendEmailNode) {
              stepData.settings = {
                template_id: newData.templateId ?? newData.settings?.template_id ?? '',
                email_account_id: newData.emailAccountId ?? newData.settings?.email_account_id ?? '',
                recipient_source: newData.recipientSource ?? newData.settings?.recipient_source ?? 'variable',
                recipient_email: newData.recipientEmail ?? newData.settings?.recipient_email ?? '',
                variable_mappings: newData.variableMappings ?? newData.settings?.variable_mappings ?? {},
                message_override: newData.messageOverride ?? newData.settings?.message_override ?? '',
                subject_override: newData.subjectOverride ?? newData.settings?.subject_override ?? '',
                use_template_message: typeof newData.useTemplateMessage === 'boolean'
                  ? newData.useTemplateMessage
                  : Boolean(newData.settings?.use_template_message ?? true),
                use_template_subject: typeof newData.useTemplateSubject === 'boolean'
                  ? newData.useTemplateSubject
                  : Boolean(newData.settings?.use_template_subject ?? true),
              }
            }

            const mergedStepData = { ...node?.data, ...newData }
            stepData.options = {
              expanded: mergedStepData.expanded ?? true,
              autoLayoutMode: mergedStepData.autoLayoutMode ?? false,
              handlePosition: mergedStepData.handlePosition ?? null,
              dynamicDateFormat: mergedStepData.dynamicDateFormat ?? DEFAULT_DYNAMIC_DATE_FORMAT,
            }

            const response = await fetch(`/workflows/${resolvedWorkflowId}/steps/${actualNodeId}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrfToken,
                'Accept': 'application/json',
              },
              body: JSON.stringify(stepData),
            })

            if (!response.ok) {
              throw new Error(`Failed to update step: ${response.status}`)
            }

            if (nodeStepSaveGenerationRef.current.get(nodeId) === saveGeneration) {
              setNodes((nds) =>
                nds.map((node) => {
                  if (node.id !== nodeId) return node
                  const mergedSettings = stepData.settings ?? node.data?.settings
                  const mergedData = {
                    ...node.data,
                    ...newData,
                    ...(mergedSettings ? { settings: mergedSettings } : {}),
                  }

                  if (mergedSettings && (node.type === 'updateJobNode' || stepData.step_type === 'update_job')) {
                    mergedData.operation = mergedSettings.operation ?? mergedData.operation
                    mergedData.jobId = mergedSettings.job_id ?? mergedData.jobId
                    mergedData.jbsId = mergedSettings.jbs_id ?? mergedData.jbsId
                    mergedData.jobStatusLabel = mergedSettings.job_status_label ?? mergedData.jobStatusLabel
                  }

                  return {
                    ...node,
                    data: mergedData,
                  }
                }),
              )
            }
          } catch (error) {
            console.error('Failed to save node data to database:', error)
          }
        })

      nodeStepSaveQueueRef.current.set(nodeId, saveTask)
      return
    }
  }

  // Update nodes to include the callback functions
  const nodeCallbacksRef = useRef({
    onOpenTriggersPanel: () => {},
    onOpenConnectionMenu: () => {},
    onNodeDataChange: async () => {},
    onNodeStateChange: () => {},
  })

  nodeCallbacksRef.current = {
    onOpenTriggersPanel: handleOpenTriggersPanel,
    onOpenConnectionMenu: handleOpenConnectionMenu,
    onNodeDataChange: handleNodeDataChange,
    onNodeStateChange: handleNodeExpandedChange,
  }

  const stableOnOpenTriggersPanel = useCallback((...args) => {
    nodeCallbacksRef.current.onOpenTriggersPanel(...args)
  }, [])

  const stableOnOpenConnectionMenu = useCallback((...args) => {
    nodeCallbacksRef.current.onOpenConnectionMenu(...args)
  }, [])

  const stableOnNodeDataChange = useCallback((...args) => {
    nodeCallbacksRef.current.onNodeDataChange(...args)
  }, [])

  const stableOnNodeStateChange = useCallback((...args) => {
    nodeCallbacksRef.current.onNodeStateChange(...args)
  }, [])

  useEffect(() => {
    setNodes((nds) => {
      let changed = false
      const next = nds.map((node) => {
        if (
          node.data.onOpenTriggersPanel === stableOnOpenTriggersPanel &&
          node.data.onOpenConnectionMenu === stableOnOpenConnectionMenu &&
          node.data.onNodeDataChange === stableOnNodeDataChange &&
          node.data.onNodeStateChange === stableOnNodeStateChange
        ) {
          return node
        }

        changed = true
        return {
          ...node,
          data: {
            ...node.data,
            onOpenTriggersPanel: stableOnOpenTriggersPanel,
            onOpenConnectionMenu: stableOnOpenConnectionMenu,
            onNodeDataChange: stableOnNodeDataChange,
            onNodeStateChange: stableOnNodeStateChange,
          },
        }
      })

      return changed ? next : nds
    })
  }, [
    currentWorkflow,
    nodesInitialized,
    nodes.length,
    stableOnOpenTriggersPanel,
    stableOnOpenConnectionMenu,
    stableOnNodeDataChange,
    stableOnNodeStateChange,
    setNodes,
  ])

  // Update edge data to keep nodes data in sync for edge validation
  useEffect(() => {
    setEdges((eds) => {
      let changed = false
      const next = eds.map((edge) => {
        if (edge.data?.nodes === nodes) {
          return edge
        }

        changed = true
        return {
          ...edge,
          data: { ...edge.data, nodes },
        }
      })

      return changed ? next : eds
    })
  }, [nodes, setEdges])

  const handleBackToList = () => {
    router.visit("/workflows")
  }

  // --- Layout logic for view modes ---
  const layoutNodes = useCallback(
    (mode) => {
      if (!setNodes || !nodes || !currentWorkflow) return
      const defaultWidth = 250
      const defaultHeight = 120
      const gap = 48
      if (mode === "graph") {
        // Horizontal layout
        const nodeWidths = nodes.map((node) => node.width || defaultWidth)
        const totalWidth = nodeWidths.reduce((sum, w) => sum + w, 0) + gap * (nodes.length - 1)
        const reactFlowContainer = document.querySelector(".react-flow")
        const containerWidth = reactFlowContainer ? reactFlowContainer.clientWidth : 1200
        const startX = Math.max(100, (containerWidth - totalWidth) / 2)
        const y = 200
        let currentX = startX
        setNodes((nds) =>
          nds.map((node, idx) => {
            const width = node.width || defaultWidth
            const newNode = {
              ...node,
              position: {
                x: currentX,
                y,
              },
              positionAbsolute: undefined,
            }
            currentX += width + gap
            return newNode
          }),
        )

        // Save all node positions after layout
        nodes.forEach((node) => {
          const width = node.width || defaultWidth
          const newPosition = {
            x: currentX,
            y,
          }
          currentX += width + gap
          updateNode(currentWorkflow.id, node.id, { position: newPosition })
        })
      } else {
        // Tree (vertical) layout
        const nodeHeights = nodes.map((node) => node.height || defaultHeight)
        // Calculate total height including gaps between nodes
        const totalHeight = nodeHeights.reduce((sum, h) => sum + h, 0) + gap * (nodes.length - 1)
        const reactFlowContainer = document.querySelector(".react-flow")
        const containerHeight = reactFlowContainer ? reactFlowContainer.clientHeight : 800
        const x = 300
        const startY = Math.max(100, (containerHeight - totalHeight) / 2)
        let currentY = startY

        // First pass: calculate positions
        const nodePositions = nodes.map((node, idx) => {
          const height = node.height || defaultHeight
          const position = {
            x,
            y: currentY,
          }
          currentY += height + gap
          return { node, position }
        })

        // Second pass: update nodes with calculated positions
        setNodes((nds) =>
          nds.map((node, idx) => {
            const { position } = nodePositions[idx]
            return {
              ...node,
              position,
              positionAbsolute: undefined,
            }
          }),
        )

        // Save all node positions after layout
        nodePositions.forEach(({ node, position }) => {
          updateNode(currentWorkflow.id, node.id, { position })
        })
      }
    },
    [setNodes, nodes, reactFlowInstance, currentWorkflow, updateNode],
  )

  // --- Handle view mode change ---
  const handleViewModeChange = (mode) => {
    setViewMode(mode)
    if (currentWorkflow) {
      updateWorkflow(currentWorkflow.id, { viewMode: mode })
    }
    layoutNodes(mode)
  }

  // Add auto layout function
  const handleAutoLayout = useCallback(() => {
    if (!currentWorkflow) return
    layoutNodes(viewMode)
  }, [currentWorkflow, layoutNodes, viewMode])

  // Editable title logic
  const startEditingTitle = () => {
    setTempWorkflowName(workflowName)
    setIsEditingName(true)
    setTimeout(() => {
      titleInputRef.current?.focus()
    }, 0)
  }

  const saveWorkflowTitle = async () => {
    const newName = tempWorkflowName.trim() || workflowName
    
    if (!currentWorkflow) {
      setIsEditingName(false);
      return;
    }

    if (newName === workflowName) {
      setIsEditingName(false);
      return;
    }

    try {
      // Call the async updateWorkflow function
      await updateWorkflow(currentWorkflow.id, { name: newName }, false, csrf_token);
      
      // Update local state
      setWorkflowName(newName);
      setIsEditingName(false);
      setHasChanges(true);
      
      // Show success toast
      toast({
        title: "Workflow name updated",
        description: `Workflow renamed to "${newName}".`,
      })
      
    } catch (error) {
      console.error('Error updating workflow name:', error);
      // Revert the temporary name on error
      setTempWorkflowName(workflowName);
      setIsEditingName(false);
      
      // Show error toast
      toast({
        title: "Name update failed",
        description: "There was an error updating the workflow name. Please try again.",
        variant: "destructive",
      })
    }
  }

  const cancelEditingTitle = () => {
    setIsEditingName(false)
  }

  const handleTitleKeyDown = async (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      await saveWorkflowTitle();
    }
    if (e.key === "Escape") {
      cancelEditingTitle();
    }
  }

  return (
    <div className="app-container">
      <div className="workflow-header flex items-center justify-between px-4 h-12 border-b bg-white dark:bg-card dark:border-border">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={handleBackToList} className="h-8 w-8">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center">
            {isEditingName ? (
              <div className="flex items-center">
                <Input
                  ref={titleInputRef}
                  value={tempWorkflowName}
                  onChange={(e) => setTempWorkflowName(e.target.value)}
                  onKeyDown={handleTitleKeyDown}
                  className="h-7 text-sm font-medium"
                />
                <Button variant="ghost" size="icon" onClick={async () => await saveWorkflowTitle()} className="h-7 w-7 ml-1">
                  <Check className="h-3 w-3" />
                </Button>
                <Button variant="ghost" size="icon" onClick={cancelEditingTitle} className="h-7 w-7">
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <h1
                className="text-sm font-medium truncate cursor-pointer hover:text-primary flex items-center"
                onClick={startEditingTitle}
              >
                {workflowName}
                <Edit2 className="ml-2 h-3 w-3 opacity-50" />
              </h1>
            )}
            {currentWorkflow?.status && (
              <Badge variant={getStatusBadgeVariant(currentWorkflow.status)} className="text-xs ml-3 dark:text-white">
                {currentWorkflow.status.charAt(0).toUpperCase() + currentWorkflow.status.slice(1)}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex gap-2 items-center">
          {hasChanges && <span className="text-xs text-muted-foreground">Undeployed changes</span>}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setMockExecutionOpen(true)} 
            className="h-8 dark:bg-white dark:text-black dark:border-border"
          >
            <Play className="h-3 w-3 mr-1" />
            Test
          </Button>
          {selectedEdges.length > 0 && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRunSelectedFlows} 
              className="h-8 dark:bg-white dark:text-black dark:border-border"
            >
              <Play className="h-3 w-3 mr-1" />
              Run Selected Flows ({selectedEdges.length})
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleRunWorkflow} className="h-8 dark:bg-white dark:text-black dark:border-border">
            Run
          </Button>
          <Popover open={deployPopoverOpen} onOpenChange={setDeployPopoverOpen}>
            <PopoverTrigger asChild>
              <Button size="sm" className="h-8 dark:text-white" onClick={() => setDeployPopoverOpen(true)}>
                {getButtonConfig().text}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0 relative" align="end">
              {/* X icon in top right */}
              <button
                aria-label="Close popover"
                className="absolute top-2 right-2 p-1 rounded hover:bg-muted transition-colors"
                onClick={() => {
                  setDeployPopoverOpen(false)
                  setWorkflowDescription(currentWorkflow?.description || "") // Reset description
                }}
                tabIndex={0}
                type="button"
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
              <div className="p-4 pb-0">
                {currentWorkflow?.status === "active" ? (
                  <>
                    <h3 className="font-medium text-sm mb-3">Manage workflow</h3>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Status</span>
                          <Badge variant="default" className="text-xs">
                            Active
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Last deployed</span>
                          <span className="text-xs">{formatDate(currentWorkflow?.updatedAt)}</span>
                        </div>
                      </div>
                      <Separator />
                      <div className="space-y-2">
                        <Label htmlFor="description" className="text-xs font-normal">
                          Description
                        </Label>
                        <Textarea
                          id="description"
                          placeholder="Add a description for this workflow"
                          value={workflowDescription}
                          onChange={(e) => setWorkflowDescription(e.target.value)}
                          className="text-xs font-normal"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <h3 className="font-medium text-sm mb-3">Deploy workflow</h3>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="description" className="text-xs font-normal">
                          Description
                        </Label>
                        <Textarea
                          id="description"
                          placeholder="Add a short description of changes (optional)"
                          value={workflowDescription}
                          onChange={(e) => setWorkflowDescription(e.target.value)}
                          className="text-xs font-normal"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="flex items-center justify-between p-4 pt-0 mt-4">
                {currentWorkflow?.status === "active" ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDeactivateWorkflow}
                      className="flex items-center gap-1"
                    >
                      <PowerOff className="h-3 w-3" />
                      Deactivate
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleUpdateDescription}
                      disabled={workflowDescription === currentWorkflow?.description}
                    >
                      Update
                    </Button>
                  </>
                ) : (
                  <div className="flex justify-end w-full">
                    <Button 
                      size="sm" 
                      onClick={handleConfirmDeploy}
                      className="flex items-center gap-1 dark:text-white"
                    >
                      <Power className="h-3 w-3" />
                      Deploy
                    </Button>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      <div className="app-content">
        <Sidebar
          onSelectBlock={addBlockToWorkflow}
          onOpenTriggersPanel={handleOpenTriggersPanel}
          onOpenBlocksPanel={handleOpenBlocksPanel}
          onOpenOutlinePanel={handleOpenOutlinePanel}
          onOpenHistoryPanel={handleOpenHistoryPanel}
          nodes={nodes}
          edges={edges}
          onNodeSelect={(nodeId) => {
            // Find the node
            const node = nodes.find((n) => n.id === nodeId)
            if (node && reactFlowInstance) {
              // Center the view on the selected node
              reactFlowInstance.setCenter(
                node.position.x + 100, // Add offset to center the node
                node.position.y + 50,
                { duration: 800 }, // Animation duration
              )

              // Optionally highlight the node (you would need to implement this)
              // setHighlightedNode(nodeId);
            }
          }}
          openPanel={openPanel}
          showBlocksPanel={showBlocksPanel}
          showTriggersPanel={showTriggersPanel}
          showOutlinePanel={showOutlinePanel}
          showHistoryPanel={showHistoryPanel}
          setShowBlocksPanel={setShowBlocksPanel}
          setShowTriggersPanel={setShowTriggersPanel}
          setShowOutlinePanel={setShowOutlinePanel}
          setShowHistoryPanel={setShowHistoryPanel}
          workflowId={workflowId || workflow?.id}
          permissions={permissions}
          twilio={twilio}
          canSendTexts={canSendTexts}
          canSendEmails={canSendEmails}
        />
        <div className="workflow-container" ref={reactFlowWrapper} onClick={handleWorkflowClick}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onNodeDragStop={handleNodeDragStop}
            onEdgesChange={handleEdgesChange}
            onConnect={handleConnect}
            onConnectStart={onConnectStart}
            onConnectEnd={handleConnectEnd}
            onInit={onInit}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeMouseEnter={handleNodeMouseEnter}
            onNodeMouseLeave={handleNodeMouseLeave}
            onEdgeClick={handleEdgeClick}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            defaultViewport={initialViewport}
            minZoom={0.01}
            maxZoom={50}
            zoomOnScroll={false}
            zoomOnPinch={false}
            zoomOnDoubleClick={false}
            panOnScroll={!isChecklistInteracting}
            connectionLineType={ConnectionLineType.Straight}
            connectionLineStyle={CONNECTION_LINE_STYLE}
            connectionLineComponent={CustomConnectionLine}

            nodesDraggable={true}
            nodesConnectable={true}
            elementsSelectable={true}
            noDragClassName="nodrag"
            proOptions={PRO_OPTIONS}
            deleteKeyCode={DELETE_KEY_CODE}
            multiSelectionKeyCode={MULTI_SELECTION_KEY_CODE}
            snapToGrid={true}
            snapGrid={SNAP_GRID}
            onlyRenderVisibleElements={true}
            preventScrolling={true}
            style={{ height: "100%", width: "100%" }}
            className="workflow-canvas"
            elevateNodesOnSelect={true}
            autoPanOnConnect={true}
            autoPanOnNodeDrag={true}
            attributionPosition="bottom-right"
          >
            {showMiniMap && <MiniMap/>}
            <Background 
              variant="dots" 
              gap={12} 
              size={1} 
              style={{ backgroundColor: getBackgroundColor() }}
            />
            <Panel position="top-left">
              <NodePalette />
            </Panel>
            {showMapButton && (
              <Panel position="bottom-right">
                <button className="minimap-toggle-button dark:text-white dark:bg-card dark:border-none" onClick={toggleMiniMap} title="Show overview">
                  <MapIcon size={18} />
                </button>
              </Panel>
            )}
            <ZoomControls 
              reactFlowInstance={reactFlowInstance} 
              nodes={nodes} 
              setNodes={setNodes}
              edges={edges}
              setEdges={setEdges}
              currentWorkflow={currentWorkflow}
              workflowId={workflow?.id?.toString() || currentWorkflowId || currentWorkflow?.id}
              updateNode={updateNode}
              onPersistNodeGraph={persistNodeGraph}
              pasteNode={pasteNode}
              hasCopiedNode={hasCopiedNode}
              getCopiedNodeInfo={getCopiedNodeInfo}
              csrf_token={csrf_token}
              toast={toast}
            />
            {/* View mode toggle icons as a Panel, top-center */}
            {/* <Panel position="top-center">
              <div
                style={{
                  background: "#fff",
                  borderRadius: 12,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  display: "flex",
                  gap: 8,
                  padding: 4,
                }}
              >
                <button
                  onClick={() => handleViewModeChange("graph")}
                  style={{
                    background: viewMode === "graph" ? "#f3f4f6" : "transparent",
                    border: "none",
                    borderRadius: 8,
                    padding: 6,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                  title="Graph View (Left to Right)"
                >
                  <Layout size={15} stroke={viewMode === "graph" ? "#6366f1" : "#888"} />
                </button>
                <button
                  onClick={() => handleViewModeChange("tree")}
                  style={{
                    background: viewMode === "tree" ? "#f3f4f6" : "transparent",
                    border: "none",
                    borderRadius: 8,
                    padding: 6,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                  title="Tree View (Top to Bottom)"
                >
                  <GitFork size={15} stroke={viewMode === "tree" ? "#6366f1" : "#888"} />
                </button>
              </div>
            </Panel> */}
          </ReactFlow>
          {showConnectionMenu && (
            <ConnectionBlocksMenu
              position={connectionMenuPosition}
              onClose={handleCloseConnectionMenu}
              onSelectBlock={(blockId) => {
                addBlockToWorkflow(blockId)
                handleCloseConnectionMenu()
              }}
            />
          )}
        </div>
      </div>
      {showTriggersPanel && (
        <TriggersPanel
          onClose={() => {
            setShowTriggersPanel(false)
            setActiveTrigger(null)
          }}
          csrf_token={csrf_token}
          initialTriggerId={activeTrigger}
          initialEnabledTriggers={triggersContext.enabledTriggers || enabledTriggers}
          workflowId={triggersContext.workflowId}
          nodeId={triggersContext.nodeId}
          onEnabledTriggersChange={(enabledTriggers) => {
            setEnabledTriggers(enabledTriggers)
            // Update the specific trigger node with the new enabled triggers
            setNodes((nds) =>
              nds.map((node) =>
                node.id === triggersContext.nodeId 
                  ? { ...node, data: { ...node.data, enabledTriggers } } 
                  : node,
              ),
            )
            
            // Update the workflow context to reflect the new trigger state
            if (currentWorkflow) {
              updateNode(currentWorkflow.id, triggersContext.nodeId, {
                data: { enabledTriggers }
              })
            }
          }}
        />
      )}

      {showHistoryPanel && (
        <WorkflowHistoryPanel
          onClose={() => setShowHistoryPanel(false)}
          workflowHistory={workflowHistory}
          pagination={historyPagination}
          loading={historyLoading}
          error={historyError}
          onRefresh={fetchWorkflowHistory}
          users={users}
          csrf_token={csrf_token}
          workflowId={currentWorkflow?.id}
          categories={categories}
        />
      )}

      {/* Mock Execution Modal */}
      <MockExecutionModal
        open={mockExecutionOpen}
        onOpenChange={setMockExecutionOpen}
        workflow={currentWorkflow}
      />
    </div>
  )
}

export default function Builder(props) {
  // Move window.users assignment to useEffect
  useEffect(() => {
    window.users = props.users;
  }, [props.users]);

  // Move window.categories assignment to useEffect
  useEffect(() => {
    window.categories = props.categories;
  }, [props.categories]);

  // Only log in development
  if (process.env.NODE_ENV === 'development') {
    // console.log('Builder props:', props);
  }

  return (
    <>
      <Head title="DME | Workflow Builder" />
      <App {...props} />
    </>
  );
}

const styles = {
  workflowCanvas: {
    transition: "none !important",
  },
  rfDisableTransitions: {
    "*": {
      transition: "none !important",
      animation: "none !important",
    },
  },
}
